import { a as DEFAULT_THEME_MODE, c as cn, i as DEFAULT_THEME, n as ThemeScope, o as THEME_CLASS_NAME_PREFIX, r as DARK_MODE_CLASS_NAME, s as themeClassNamesOf, t as ThemeSurface } from "./theme-surface-D671Y4Kc.mjs";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Root } from "@radix-ui/react-label";
import { Root as Root$1, Thumb } from "@radix-ui/react-switch";
import { Root as Root$2 } from "@radix-ui/react-separator";
import { Corner, Root as Root$3, ScrollAreaScrollbar, ScrollAreaThumb, Viewport } from "@radix-ui/react-scroll-area";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { CheckCircle2, CircleX, Info, TriangleAlert, X } from "lucide-react";
//#region src/components/button.tsx
/**
* The classes a `Button` carries, by variant and by size (R10.5).
*
* `class-variance-authority` rather than a pair of hand-kept records, which is
* both the shadcn idiom and the shape that puts the defaults beside the variants
* they belong to: `defaultVariants` is the single statement of what a caller who
* supplies nothing gets, and the component below no longer restates it.
*
* The colour vocabulary is shadcn's — `bg-primary`, `text-primary-foreground`,
* `bg-accent` — and it resolves through the alias block in `src/tokens.css`, which
* points each of those names at one of the fifteen semantic roles. Nothing here is a
* literal colour. The spacing, radius, typography and duration utilities are the
* platform's `hurdl-` scales rather than Tailwind's default scale, so shadcn's
* `h-9 px-4 py-2 gap-2 text-sm rounded-md` becomes the token equivalents:
* R10.5 forbids a literal length in this file, and `px-4` is a length by another
* name. Height follows from the padding rather than being set, which is why there
* is no `h-*` utility to translate.
*
* Three entries carry more explanation than the rest.
*
* `duration-(--duration-hurdl-medium)` is the CSS-variable shorthand rather than a
* `duration-hurdl-medium` utility, because `--duration-*` is not a Tailwind theme
* namespace and no such utility exists; `src/tokens.css` records the finding and
* the consequence in full. Referencing the token this way is what makes the
* reduced-motion preference apply without a line of JavaScript: the stylesheet
* redeclares the token as `0ms` under `prefers-reduced-motion`, and this
* transition takes the new value without knowing the preference exists.
*
* `disabled:opacity-50` is the one value here that is not a token reference, and it
* is not one because there is no opacity Design_Token to reference. R10.5 governs
* colour, spacing, border radius, typography and transition duration, and a
* disabled control has to be distinguishable from an enabled one; expressing that
* with the semantic colours instead would mean repainting a disabled `Button` in
* another role's palette, which reads as a different button rather than as an
* unavailable one.
*
* `[&_svg]:size-hurdl-4` is shadcn's icon sizing with the platform's spacing scale
* substituted for `size-4`. shadcn's `shadow-xs` on its solid variants becomes
* `shadow-hurdl-low`, carried by the three filled variants: elevation is a
* Design_Token family of its own since ADR 0004, and a shadow expressed in
* Tailwind's default scale would be a literal length.
*/
const buttonClassNames = cva([
	"inline-flex shrink-0 items-center justify-center whitespace-nowrap",
	"font-hurdl-sans font-hurdl-semibold leading-hurdl-tight",
	"rounded-hurdl-medium",
	"transition-colors duration-(--duration-hurdl-medium)",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
	"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-hurdl-4"
].join(" "), {
	variants: {
		/**
		* Each hover state darkens or lightens the variant's own fill, which is what
		* shadcn does with `hover:bg-primary/90` and what this palette does with a
		* token instead: the two action roles each declare a `-hover` fill, so the
		* hover colour is measured rather than composited from an opacity, and the
		* foreground stays the role's own — a hover changes one colour, not two.
		* `test/contrast.property.test.ts` holds each action foreground to 4.5:1
		* against the hover fill as well as the resting one, so a control cannot
		* become unreadable at the moment it is pointed at.
		*
		* Earlier this inverted the pair — foreground became background — because the
		* palette had one foreground per background and nothing else it could offer.
		* That read as a different button on hover rather than the same button
		* responding. ADR 0004 records the palette rebuild that removed the need.
		*
		* `secondary` and `ghost` are unfilled or lightly filled, so both hover onto
		* `accent`, the surface role that exists for exactly that: shadcn's
		* `hover:bg-accent hover:text-accent-foreground`, unchanged.
		*
		* The shadow is on the three filled variants and not on `ghost`, which paints
		* no surface and so has nothing to cast one.
		*/
		variant: {
			primary: ["bg-primary text-primary-foreground shadow-hurdl-low", "hover:bg-primary-hover"].join(" "),
			secondary: [
				"border border-input shadow-hurdl-low",
				"bg-secondary text-secondary-foreground",
				"hover:bg-accent hover:text-accent-foreground"
			].join(" "),
			destructive: ["bg-destructive text-destructive-foreground shadow-hurdl-low", "hover:bg-destructive-hover"].join(" "),
			ghost: "text-foreground hover:bg-accent hover:text-accent-foreground"
		},
		/**
		* Horizontal padding, vertical padding, the gap between a `Button`'s
		* children, and the font size.
		*
		* `secondary` is the only variant carrying a border, and it changes none of
		* these measurements: Tailwind's preflight sets `box-sizing: border-box`, so
		* the border is drawn inside the box the padding already established and a
		* row of buttons of mixed variants stays aligned.
		*/
		size: {
			small: "gap-hurdl-1 px-hurdl-2 py-hurdl-1 text-hurdl-caption",
			medium: "gap-hurdl-2 px-hurdl-3 py-hurdl-2 text-hurdl-body",
			large: "gap-hurdl-2 px-hurdl-6 py-hurdl-3 text-hurdl-body"
		}
	},
	defaultVariants: {
		variant: "primary",
		size: "medium"
	}
});
/**
* A `button` element (R10.1, R10.3).
*
* ```tsx
* <Button onClick={save}>Save</Button>
* <Button variant="destructive" size="small" onClick={remove}>Remove</Button>
* <Button disabled>Saving…</Button>
* <Button asChild><a href="/settings">Settings</a></Button>
* ```
*
* Everything the control *does* is the user agent's. `disabled` reaches the
* rendered element as the native attribute, and a disabled `button` dispatches no
* `click` event for a pointer press, for `Enter` or for `Space`, so R10.6 holds
* without this component reading the property for any purpose other than its
* appearance. There is no `aria-disabled` with a suppressed handler here, which is
* the usual way that guarantee is lost.
*
* `data-slot="button"` is shadcn's convention and is what makes a `Button` findable
* from a surrounding component's styles — the way `Card` reaches its own children —
* without either component exporting a class name to the other.
*
* `className` is composed through `cn` with the defaults first (R10.4), so a
* supplied utility overrides the default it conflicts with and every default it
* does not conflict with survives. `variant` and `size` remain the supported way to
* change a `Button`'s appearance, because they are the only way that keeps the
* colour pairs the palette measured.
*/
function Button({ variant = "primary", size = "medium", asChild = false, className, ...properties }) {
	return /* @__PURE__ */ jsx(asChild ? Slot : "button", {
		"data-slot": "button",
		...properties,
		className: cn(buttonClassNames({
			variant,
			size
		}), className)
	});
}
//#endregion
//#region src/components/input.tsx
/**
* The classes every `Input` carries (R10.5).
*
* shadcn's colour vocabulary, resolved through the alias block in
* `src/tokens.css`, with the platform's `hurdl-` scales in place of Tailwind's
* default scale for every measurement: shadcn's `h-9 px-3 py-1 text-base rounded-md`
* becomes the token equivalents, and the height follows from the padding rather
* than being set, because R10.5 forbids a literal length in this file.
*
* Two deliberate departures from shadcn's source.
*
* shadcn writes `bg-transparent` and sets no text colour, so a field takes both
* from whatever surface it sits on. This package declares the pair explicitly —
* `bg-background text-foreground`, which the alias block resolves to the neutral
* role — because the palette measures the contrast of a *named pair* (R9.9), and a
* field inheriting its text colour onto an inherited background is a pair nobody
* measured. A field inside a warning `Alert` is the case that goes wrong.
*
* shadcn's focus ring is `ring-ring/50 ring-[3px]`. Both are dropped: `ring-[3px]`
* is a literal length, and a half-transparent ring is a colour the contrast bound
* never saw. `ring` with the opaque `ring-ring` is the platform's focus indicator,
* and the focus colour is the one R9.9 holds to 3:1 against every background.
*
* `disabled:opacity-50` is the one value that is not a token reference, there being
* no opacity Design_Token to reference; `button.tsx` carries the reasoning.
*
* `w-full` rather than a width: the field fills its container, and the container
* decides how wide that is. A `Stack` or a `Card` therefore governs the measure of
* a form, which is where that decision belongs and how it stays consistent between
* one form and the next.
*/
const CLASS_NAMES$1 = [
	"flex w-full min-w-0",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-tight",
	"rounded-hurdl-medium border border-input px-hurdl-3 py-hurdl-2",
	"bg-card text-card-foreground",
	"placeholder:text-muted-foreground",
	"selection:bg-primary selection:text-primary-foreground",
	"transition-colors duration-(--duration-hurdl-medium)",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"aria-invalid:border-destructive",
	"disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
	"file:inline-flex file:border-0 file:bg-transparent",
	"file:font-hurdl-semibold file:text-hurdl-caption file:text-foreground"
].join(" ");
/**
* An `input` element (R10.1, R10.3).
*
* ```tsx
* <Input type="email" value={address} onChange={onAddressChange} />
* <Input invalid defaultValue="not-an-address" />
* ```
*
* `aria-invalid` is written before the caller's properties are spread, so an
* `aria-invalid` the caller supplies — the one a `FormField` passes to its child —
* wins over the one `invalid` derives, and the border follows whichever won.
*
* `data-slot="input"` is shadcn's convention, and it is what lets a surrounding
* component reach the field it wraps without either component naming a class the
* other owns.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function Input({ invalid = false, className, ...properties }) {
	return /* @__PURE__ */ jsx("input", {
		"aria-invalid": invalid ? true : void 0,
		"data-slot": "input",
		...properties,
		className: cn(CLASS_NAMES$1, className)
	});
}
//#endregion
//#region src/components/label.tsx
/**
* The classes every `Label` carries (R10.5).
*
* shadcn's own class list with the platform's scales substituted for Tailwind's —
* `text-sm font-medium gap-2` becoming the token equivalents — and one departure
* worth naming: `inline-flex` rather than shadcn's `flex`, so that a label is as
* wide as its text rather than as wide as its container. That is the behaviour this
* package's `Label` already had, a block-level label would change the appearance of
* every existing call site, and nothing in the shadcn source depends on the label
* filling its row.
*
* The gap separates the required indicator from the label text with a spacing
* Design_Token, like everything else the Design_System spaces, and it means a label
* wrapping onto a second line keeps the indicator beside the last word rather than
* orphaning it.
*
* `select-none` and the two disabled variants are shadcn's, and both are inherited
* behaviour rather than decoration: a label whose text can be selected by a
* double-click on the control it names is a nuisance, and a label beside a disabled
* control should look disabled without being told that the control is.
* `opacity-50` matches `Button` and `Input`, and is the one value here that is not a
* token reference, there being no opacity Design_Token; `button.tsx` carries the
* reasoning.
*/
const CLASS_NAMES = [
	"inline-flex select-none items-center gap-hurdl-1",
	"font-hurdl-sans font-hurdl-semibold text-hurdl-caption leading-hurdl-tight",
	"text-foreground",
	"group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50",
	"peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
].join(" ");
/**
* The classes the required indicator carries: the destructive role's foreground, on
* whatever surface the label sits on.
*
* The role is the one the palette gives to something the user must attend to, and it
* is the same colour a rejected value is outlined in, so the indicator and the error
* state of the field it names read as one convention rather than two.
*/
const REQUIRED_INDICATOR_CLASS_NAMES = "text-destructive-foreground";
/**
* The character a required field is marked with.
*
* An asterisk, which is the convention users already know, and named here so that
* changing it is one edit rather than a search through the components.
*/
const REQUIRED_INDICATOR = "*";
/**
* A `label` element (R10.1, R10.3).
*
* ```tsx
* <Label htmlFor="electronic-mail-address" required>
*   Electronic mail address
* </Label>
* ```
*
* The element is Radix's label primitive, and it renders a `label` — verified by
* `test/component-rendering.test.tsx`, which asserts the tag rather than trusting
* the primitive. What Radix adds is one behaviour a bare `label` does not have: it
* suppresses the text selection a double-click on the label would otherwise leave
* behind while activating the control. Nothing about the association changes, so
* R10.3 holds for the same reason it did before.
*
* The required indicator carries `aria-hidden`, so the label's accessible name is
* the text the caller supplied and nothing else. Announcing the asterisk would
* append a stray character to every required field's name, and the requirement is
* already announced from the control's own `required` attribute; hiding it removes
* the duplicate rather than the information.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function Label({ required = false, className, children, ...properties }) {
	return /* @__PURE__ */ jsxs(Root, {
		"data-slot": "label",
		...properties,
		className: cn(CLASS_NAMES, className),
		children: [children, required ? /* @__PURE__ */ jsx("span", {
			"aria-hidden": "true",
			className: REQUIRED_INDICATOR_CLASS_NAMES,
			children: REQUIRED_INDICATOR
		}) : null]
	});
}
//#endregion
//#region src/components/instant-display.tsx
/**
* The rendered text for an instant, in the given locale and zone, or the instant
* unchanged where the runtime cannot parse it.
*
* The fallback is deliberate and is the behaviour `session-list.tsx` carried before
* this component existed: an unparseable value is more use shown as it arrived than
* as the string `Invalid Date` a bare `toLocaleString` would produce.
*/
function formatInstant(instant, locale, timeZone, format) {
	const at = new Date(instant);
	if (Number.isNaN(at.getTime())) return instant;
	return at.toLocaleString(locale, {
		...format,
		timeZone
	});
}
/**
* A `time` element rendering a fixed-width instant in a reader's locale and time
* zone, carrying the machine-readable value in `dateTime` (R19.5).
*
* ```tsx
* <InstantDisplay instant={entry.recordedAt} />
* <InstantDisplay instant={entry.recordedAt} timeZone="UTC" locale="en-GB" />
* <InstantDisplay instant={entry.recordedAt} format={{ dateStyle: 'long' }} />
* ```
*
* It reads no clock: it renders the instant it is handed and computes no interval
* against the present, so `"three days ago"` is deliberately not something it can
* produce — that would need the current time, and two renders of one instant would
* then differ. It assumes no zone either; absent a `timeZone`, an instant reads in
* the zone of whoever is looking, which is what a reader expects of a time on a page.
*
* The `dateTime` attribute carries the instant unchanged, so the value a machine
* reads — an assistive technology, a copy-paste into another tool, a future
* relative-time enhancement — is the exact instant rather than the localised prose
* beside it. That is the whole reason this is a `time` and not a `span`.
*
* It declares no colour, no size and no spacing of its own, taking the typography and
* colour of the surface it sits on, as `Stack` does and for the same reason: an
* instant appears inside a table cell, a list row or a paragraph, and it should read
* as the text around it rather than announce itself. `className` is composed through
* `cn` so a caller can still place it, with nothing to override.
*/
function InstantDisplay({ instant, locale, timeZone, format, className, ...properties }) {
	return /* @__PURE__ */ jsx("time", {
		"data-slot": "instant-display",
		...properties,
		dateTime: instant,
		className: cn(className),
		children: formatInstant(instant, locale, timeZone, format)
	});
}
//#endregion
//#region src/components/card.tsx
/**
* The classes every `Card` carries (R10.5).
*
* shadcn's surface — `bg-card text-card-foreground flex flex-col rounded-xl border` —
* with the platform's radius scale in place of `rounded-xl` and its spacing scale in
* place of the `gap-6` that separates the parts. shadcn's `shadow-sm` becomes
* `shadow-hurdl-low`, the lowest elevation step ADR 0004 added, because a shadow in
* Tailwind's default scale would be a literal length. The boundary is therefore a
* border and a shadow together, and the border is the one `Input` and a secondary
* `Button` are also outlined in, so the three read as one convention.
*
* The colours are declared rather than inherited. The alias block resolves `card` and
* `card-foreground` to the neutral pair, which is the only colour the palette pairs
* with the neutral background (R9.9), so a nested `Card` and a top-level one are the
* same surface rather than one tinted by the other.
*
* Typography is set here too, and it is set once. A `Card` holds arbitrary content,
* most of it text belonging to no other component, and text inheriting the user
* agent's serif default inside an otherwise tokenised surface is the failure this
* prevents. `Button`, `Input` and `Label` each declare their own font size, so nothing
* they render depends on what a `Card` inherits to them; prose does, and
* `leading-hurdl-relaxed` is the line height prose reads best at.
*
* The flex column matters only once the sub-components are used: with a single child
* it is indistinguishable from a block, and with a header, a content region and a
* footer it is what puts a spacing Design_Token between them instead of a margin on
* each.
*
* No `overflow` and no width: a `Card` is as wide as its container and as tall as its
* content. Layout around it belongs to a `Stack` or to the application.
*/
const CARD_CLASS_NAMES = [
	"flex flex-col gap-hurdl-4",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-relaxed",
	"rounded-hurdl-large border border-border",
	"bg-card text-card-foreground shadow-hurdl-low"
].join(" ");
/**
* The classes each padding step contributes.
*
* `none` contributes no class at all rather than a zero-length padding utility. There
* is no zero step on the spacing Design_Token scale, so `p-0` would put a literal
* length in this file and break R10.5 for the sake of a declaration whose only effect
* is to restate the initial value. `cn` discards the empty entry, so the rendered
* attribute holds no padding class and the element has none — which is what `none`
* means. It is the step a `Card` wrapping an image or a table that supplies its own
* insets wants.
*/
const PADDING_CLASS_NAMES = {
	none: "",
	small: "p-hurdl-2",
	medium: "p-hurdl-4",
	large: "p-hurdl-6"
};
/**
* The classes {@link CardHeader} carries.
*
* shadcn's header is a container-query grid — `@container/card-header grid
* auto-rows-min grid-rows-[auto_auto] gap-1.5 px-6` — whose second column exists to
* hold a `CardAction`. This package ships no `CardAction`, so there is no second
* column to reserve, and `Card` owns its own padding, so there is no `px-6` to add.
* What remains of shadcn's header is what it is for: a title and a description
* stacked, separated by the smallest spacing step.
*/
const CARD_HEADER_CLASS_NAMES = "flex flex-col gap-hurdl-1";
/**
* The classes {@link CardTitle} carries: shadcn's `font-semibold leading-none`, with
* the platform's weight and its tightest line height.
*
* No font size and no colour. The title takes both from the `Card`, so a surface is
* described in one place, and weight alone is enough to separate a one-line heading
* from the prose beneath it — which is also why the element is a `div` rather than a
* heading. A heading enters the document outline, and a `Card` cannot know which
* level would be correct in a page it has never seen, so emitting one would risk
* breaking the heading order of every consumer. A caller who wants a heading passes
* one as the title's child.
*/
const CARD_TITLE_CLASS_NAMES = "font-hurdl-semibold leading-hurdl-tight";
/**
* The classes {@link CardDescription} carries.
*
* shadcn writes `text-muted-foreground text-sm`, and both halves resolve onto the
* palette rather than being approximated: ADR 0004 gave `muted-foreground` a value of
* its own, dimmer than every surface's paired foreground and clearing 4.5:1 on all
* four of them, so the dimming is real and the smaller size is not carrying it alone.
* `src/tokens.css` records the measured ratios beside the token.
*/
const CARD_DESCRIPTION_CLASS_NAMES = ["text-hurdl-caption leading-hurdl-relaxed", "text-muted-foreground"].join(" ");
/**
* The classes {@link CardContent} carries.
*
* shadcn's content region carries `px-6` and nothing else, and `Card` owns the
* padding here, so the region states the prose typography instead. That is not
* decoration: `CardTitle` and `CardDescription` each set a size, so the content is
* the one part of a `Card` whose typography would otherwise be decided by whichever
* of its siblings was rendered last.
*/
const CARD_CONTENT_CLASS_NAMES = "text-hurdl-body leading-hurdl-relaxed";
/**
* The classes {@link CardFooter} carries.
*
* shadcn writes `flex items-center px-6`. The padding is `Card`'s here, and a gap is
* added, because a footer holds controls rather than prose and a row of `Button`s
* with nothing between them is the arrangement every caller would otherwise have to
* correct.
*/
const CARD_FOOTER_CLASS_NAMES = "flex items-center gap-hurdl-2";
/**
* A `div` element carrying the Design_System's surface: a bordered, rounded, padded
* region in the neutral colour role (R10.1).
*
* ```tsx
* <Card>
*   <CardHeader>
*     <CardTitle>Account</CardTitle>
*     <CardDescription>How you appear to other people.</CardDescription>
*   </CardHeader>
*   <CardContent>
*     <Stack>
*       <Label htmlFor="name">Name</Label>
*       <Input id="name" />
*     </Stack>
*   </CardContent>
*   <CardFooter>
*     <Button>Save</Button>
*   </CardFooter>
* </Card>
* <Card padding="none">
*   <img src={photograph} alt="" />
* </Card>
* ```
*
* The sub-components are additive: a `Card` holding content directly is exactly what
* it was before, which is how every existing call site keeps working. They exist
* because `DESIGN_SYSTEM.md` asks for this composition by name — "Card ↓ CardHeader ↓
* CardContent ↓ CardFooter" — in preference to a `Card` configured by properties.
*
* A `div` and not a `section`, because a `section` is a landmark only when it carries
* an accessible name, and an unnamed one adds a region to the accessibility tree that
* announces nothing. A `Card` is presentation; a caller for whom it is more than that
* passes `role` and `aria-label` through the native properties and gets the landmark
* deliberately.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function Card({ padding = "medium", className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card",
		...properties,
		className: cn(CARD_CLASS_NAMES, PADDING_CLASS_NAMES[padding], className)
	});
}
/** The title and description of a {@link Card}, stacked (R10.1). */
function CardHeader({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-header",
		...properties,
		className: cn(CARD_HEADER_CLASS_NAMES, className)
	});
}
/** The name of what a {@link Card} is about, emphasised by weight (R10.1). */
function CardTitle({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-title",
		...properties,
		className: cn(CARD_TITLE_CLASS_NAMES, className)
	});
}
/** Supporting prose beneath a {@link CardTitle} (R10.1). */
function CardDescription({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-description",
		...properties,
		className: cn(CARD_DESCRIPTION_CLASS_NAMES, className)
	});
}
/** The body of a {@link Card} (R10.1). */
function CardContent({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-content",
		...properties,
		className: cn(CARD_CONTENT_CLASS_NAMES, className)
	});
}
/** The controls a {@link Card} ends with, in a row (R10.1). */
function CardFooter({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-footer",
		...properties,
		className: cn(CARD_FOOTER_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/stack.tsx
/**
* The class every `Stack` carries.
*
* One utility, and no Design_Token reference among them, because a `Stack` is
* layout and nothing else: it declares no colour, no typography and no border, so
* it takes the colour and the font of whatever surface it sits on and adds only
* the arrangement. That is what makes it composable — a `Stack` inside a `Card`
* inside another `Stack` changes appearance nowhere.
*
* Spacing between children is a `gap` rather than a margin on each child, so the
* space belongs to the container that decided upon it. A margin would leak into
* every child, would need removing from the first or the last of them, and would
* collapse against whatever the children carried themselves.
*/
const BASE_CLASS_NAMES$1 = "flex";
/**
* The class each direction contributes: the flex axis.
*
* `responsive` is the one direction that reads the viewport: it stacks vertically on
* a narrow screen and lays out horizontally from the `md` breakpoint up, which is the
* reflow every two-column arrangement needs to be usable on a phone — a list beside a
* detail on a desktop becomes a list above a detail on a mobile, with no second
* component and no media query at the call site. `md` is a *structural* breakpoint
* rather than a design value: `md:flex-row` names the flex axis, which carries no
* colour, length or duration, so R10.5 does not reach it (the token-literals scan
* admits a variant and inspects the base utility, and `flex-row` names no scale). It
* is the one place this package reads a breakpoint, and it reads Tailwind's own
* default `md` (48rem) rather than declaring a breakpoint token, because a breakpoint
* is a fact about a device's width and not a value this theme chooses.
*/
const DIRECTION_CLASS_NAMES = {
	vertical: "flex-col",
	horizontal: "flex-row",
	responsive: "flex-col md:flex-row"
};
/**
* The classes each gap step contributes.
*
* `none` contributes no class at all rather than a zero-length gap utility. There
* is no zero step on the spacing Design_Token scale, so `gap-0` would put a
* literal length in this file and break R10.5 to restate the initial value; `cn`
* discards the empty entry and the element carries no gap class, which is what
* `none` means. `card.tsx` makes the same choice for `padding`.
*/
const GAP_CLASS_NAMES = {
	none: "",
	small: "gap-hurdl-2",
	medium: "gap-hurdl-4",
	large: "gap-hurdl-6"
};
/** The class each alignment contributes: the cross-axis alignment. */
const ALIGNMENT_CLASS_NAMES = {
	start: "items-start",
	centre: "items-center",
	end: "items-end",
	stretch: "items-stretch"
};
/**
* A `div` element laying its children out along one axis, separated by a spacing
* Design_Token (R10.1).
*
* ```tsx
* <Stack>
*   <Label htmlFor="name">Name</Label>
*   <Input id="name" />
* </Stack>
* <Stack direction="horizontal" gap="small" align="centre">
*   <Button variant="secondary">Cancel</Button>
*   <Button>Save</Button>
* </Stack>
* ```
*
* `stretch` is the default alignment, and it is the default because it is the one
* the platform's fields need: `Input` renders at the width it is given, so a
* vertical `Stack` of fields fills its container only while its children are
* stretched across it. It is also the initial value of `align-items`, so the
* default arrangement is the one flexbox would have produced anyway.
*
* A `div` and not a `ul`, even for what reads as a list. A list is a semantic
* claim about the content, and the content belongs to the caller; a `Stack`
* arranging two buttons is not a list, and forcing `li` children on every use
* would make the common case wrong to satisfy the rarer one. A caller with an
* actual list passes `role` through the native attributes.
*
* `className` is composed through `cn` with the defaults first (R10.4), so a
* supplied `items-center` or `flex-row` overrides the arrangement this component
* chose rather than sitting beside it and losing to whichever rule the stylesheet
* happened to emit later.
*/
function Stack({ direction = "vertical", gap = "medium", align = "stretch", className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "stack",
		...properties,
		className: cn(BASE_CLASS_NAMES$1, DIRECTION_CLASS_NAMES[direction], GAP_CLASS_NAMES[gap], ALIGNMENT_CLASS_NAMES[align], className)
	});
}
//#endregion
//#region src/components/switch.tsx
/**
* The classes the track carries (R10.5).
*
* shadcn's own class list, adapted by the procedure `AGENTS.md` records. The colour
* vocabulary is shadcn's — `bg-primary`, `bg-input`, `ring-ring` — and resolves
* through the alias block in `src/tokens.css`; the measurements are the platform's
* `hurdl-` scales in place of shadcn's `h-5 w-9`, `border-2`, `shadow-sm` and
* `ring-[3px]`, because R10.5 forbids a literal length in this file and a step of
* Tailwind's own scale is a length by another name.
*
* ── The geometry, and why these four steps ──────────────────────────────────
*
* A switch is the one control in this package whose parts have to *agree*: the
* thumb's travel is the track's inner width less the thumb's own width, so three
* measurements have to be drawn from one scale and still come out to a fourth step
* on it. The spacing scale offers 0.25, 0.5, 0.75, 1, 1.5 and 2rem, and the
* arrangement that closes is
*
* - a track 2rem wide and 1rem tall — `w-hurdl-8 h-hurdl-4`, the widest step and
*   the 2:1 proportion a switch is recognised by,
* - 0.25rem of horizontal padding — `px-hurdl-1` — leaving 1.5rem inside,
* - a thumb of 0.75rem — `size-hurdl-3` — and therefore
* - a travel of exactly 0.75rem, which is `translate-x-hurdl-3` on the thumb below.
*
* The padding is horizontal only. The thumb is 0.75rem in a 1rem track, so
* `items-center` leaves an eighth of a rem above and below it; there is no spacing
* step of that size and there does not need to be one, because the value is
* *derived* from the two heights rather than declared. That is the difference R10.5
* cares about: a theme changing the spacing scale moves all four measurements
* together and the switch stays coherent.
*
* ── The colours ─────────────────────────────────────────────────────────────
*
* Checked is `bg-primary`: a filled track is a solid fill for a control, which is
* what an action role is for. Unchecked is `bg-input`, the role that bounds a
* control, which is the same colour a field's border is drawn in and reads as the
* off state rather than as a second action. Both are shadcn's choices, unchanged.
*
* No `-hover` fill is declared. A switch's hover state is not a change of intent —
* the control does not fill on hover, it is already filled or already empty — so
* there is nothing for `primary-hover` to replace, and inventing a hover colour
* would be a fill the palette never measured against the thumb on top of it.
*
* `transition-colors` carries the track's fill between the two states through
* `duration-(--duration-hurdl-medium)`, the CSS-variable shorthand rather than a
* `duration-hurdl-medium` utility, because `--duration-*` is not a Tailwind theme
* namespace and no such utility exists; `src/tokens.css` records the finding. It is
* also what makes the reduced-motion preference apply with no JavaScript: the
* stylesheet redeclares the token as `0ms` under `prefers-reduced-motion` and both
* transitions take the new value without knowing the preference exists.
*
* `shadow-hurdl-low` is shadcn's `shadow-sm` expressed as the elevation family ADR
* 0004 added. `focus-visible:ring-2 focus-visible:ring-ring` is the platform's
* focus indicator: shadcn's `ring-[3px] ring-ring/50` is dropped for the reason
* `input.tsx` records — an arbitrary length, and a half-transparent ring is a
* colour the contrast bound never saw.
*
* `disabled:opacity-50` is the one value here that is not a token reference, there
* being no opacity Design_Token to reference; `button.tsx` carries the reasoning.
*
* `peer` is shadcn's, and it is load-bearing rather than decorative: `Label`
* declares `peer-disabled:cursor-not-allowed peer-disabled:opacity-50`, so a label
* placed after a disabled `Switch` dims with it and neither component names a class
* the other owns.
*/
const TRACK_CLASS_NAMES = [
	"peer inline-flex shrink-0 cursor-pointer items-center",
	"h-hurdl-4 w-hurdl-8 rounded-full px-hurdl-1",
	"shadow-hurdl-low",
	"transition-colors duration-(--duration-hurdl-medium)",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"data-[state=unchecked]:bg-input data-[state=checked]:bg-primary",
	"disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
].join(" ");
/**
* The classes the thumb carries (R10.5).
*
* `bg-background` is shadcn's, and it is the page surface rather than the card one
* so that the thumb reads as the same neutral in both themes: light in the light
* theme, dark in the dark one, against a track that is saturated in both. It is a
* surface role on a surface role, so no contrast criterion applies to the pair —
* R9.9 measures a foreground against its own background — and the thumb is told
* apart from the track by its elevation as well as its fill.
*
* `shadow-hurdl-medium` is shadcn's `shadow-lg`, one step of the elevation family
* above the track's own, which is what makes the thumb read as sitting on the track
* rather than in it.
*
* Only the checked state declares a transform. shadcn writes
* `data-[state=unchecked]:translate-x-0` beside it; `translate-x-0` is a step of
* Tailwind's own scale and there is no zero step on the platform's spacing scale to
* replace it with, so the unchecked state declares nothing and takes the browser's
* own `translate: none`, which is the same position and transitions to and from the
* checked one identically.
*
* `pointer-events-none`, so a pointer press lands on the track and the primitive
* sees it wherever within the control the press fell. `ring-0` is dropped: nothing
* gives the thumb a ring, so it removed a ring that was never there.
*/
const THUMB_CLASS_NAMES$1 = [
	"pointer-events-none block size-hurdl-3 rounded-full",
	"bg-background shadow-hurdl-medium",
	"transition-transform duration-(--duration-hurdl-medium)",
	"data-[state=checked]:translate-x-hurdl-3"
].join(" ");
/**
* A switch: a control for a boolean value (R18.1).
*
* ```tsx
* <Switch aria-label="Remember this device" />
* <Switch checked={remembered} onCheckedChange={setRemembered} />
* <Switch defaultChecked disabled />
* ```
*
* The element is Radix's switch primitive, which renders a `button` of
* `type="button"` carrying `role="switch"` and `aria-checked`, and — where the
* control sits inside a `form` — a hidden checkbox beside it, so the value
* participates in a submission under the `name` the caller supplied. Everything the
* control
* *does* is therefore the primitive's and the user agent's: activation by pointer,
* by `Enter` and by `Space`, the tab order, the native disabled state, and the state
* assistive technology is told about (R18.6). Nothing here reads `checked` for any
* purpose, and nothing here reimplements a keyboard handler over a `div`, which is
* invariant 8 of `AGENTS.md` and the reason ADR 0003 chose this foundation.
*
* The accessible name is the caller's contribution, as it is for `Input`: a switch
* has no content of its own to be named by, so a call site supplies `aria-label` or
* — where the name should be visible — points a `Label` at it by `id`.
*
* `data-slot="switch"` and `data-slot="switch-thumb"` are shadcn's convention,
* unchanged, and are what let a surrounding component reach the control it wraps
* without either component naming a class the other owns.
*
* `className` is composed through `cn` with the defaults first (R10.4), so a
* supplied utility overrides the default it conflicts with and every default it does
* not conflict with survives. The thumb takes no `className`: it is the inside of
* the control rather than part of its public surface, and a caller who could move it
* could break the agreement between the travel and the track that
* {@link TRACK_CLASS_NAMES} records.
*/
function Switch({ className, ...properties }) {
	return /* @__PURE__ */ jsx(Root$1, {
		"data-slot": "switch",
		...properties,
		className: cn(TRACK_CLASS_NAMES, className),
		children: /* @__PURE__ */ jsx(Thumb, {
			"data-slot": "switch-thumb",
			className: THUMB_CLASS_NAMES$1
		})
	});
}
//#endregion
//#region src/components/badge.tsx
/**
* The classes a `Badge` carries, by tone (R10.5).
*
* `class-variance-authority`, as the shadcn source does, so the default tone is declared
* beside the tones rather than restated in the component.
*
* The tone map mixes the two colour vocabularies deliberately, because the roles it
* reaches for span both. `neutral` and `primary` are shadcn aliases — `bg-muted`,
* `bg-primary` — because those roles have shadcn names; the four status tints have no
* shadcn alias, so they are stated in the platform's own `hurdl-` terms, which is the
* same split `alert.tsx` records. Either way the aliases resolve to the same tokens.
*
* The pair is always the role's own. The palette declares exactly one foreground per
* background and measures the contrast of that pair alone (R9.9), so borrowing a colour
* from a neighbouring role would put an unmeasured pair on screen and quietly change
* what the tone means.
*
* The base classes are shadcn's chip in the platform's terms: an inline flex row so a
* glyph and a count sit on one baseline, `rounded-full` for the pill a count is read as,
* the caption size and the semibold weight a chip wants, and `px-hurdl-2 py-hurdl-1` in
* place of shadcn's `px-2 py-0.5`. `w-fit` so the chip is only as wide as its contents,
* and `shrink-0` so a `Badge` in a flex row is not squeezed below the width of the count
* it holds. `whitespace-nowrap` so a two-word label stays on the one line a chip is.
*/
const badgeClassNames = cva([
	"inline-flex w-fit shrink-0 items-center justify-center",
	"gap-hurdl-1 whitespace-nowrap rounded-full px-hurdl-2 py-hurdl-1",
	"font-hurdl-sans font-hurdl-semibold text-hurdl-caption leading-hurdl-tight"
].join(" "), {
	variants: { tone: {
		neutral: "bg-muted text-muted-foreground",
		primary: "bg-primary text-primary-foreground",
		informational: "bg-hurdl-informational-background text-hurdl-informational-foreground",
		success: "bg-hurdl-success-background text-hurdl-success-foreground",
		warning: "bg-hurdl-warning-background text-hurdl-warning-foreground",
		destructive: "bg-hurdl-error-background text-hurdl-error-foreground"
	} },
	defaultVariants: { tone: "neutral" }
});
/**
* A `span` element carrying a count or a short status label in one of six colour roles
* (R10.1).
*
* ```tsx
* <Badge>7</Badge>
* <Badge tone="primary">New</Badge>
* <Badge tone="success">Active</Badge>
* <Badge tone="destructive">Failed</Badge>
* ```
*
* The element carries no `role` of its own: a chip is text, and its meaning is the text
* it holds rather than a semantic the colour alone conveys — a sighted reader sees the
* tint, and a screen-reader user reads "7" or "Failed", which is the information. A
* caller rendering a live count that should be announced on change passes `role="status"`
* through the native attributes, which reaches the element and wins.
*
* `data-slot="badge"` is shadcn's convention, unchanged, and is what lets a surrounding
* component reach the chip it wraps without either naming a class the other owns.
*
* `className` is composed through `cn` with the defaults first (R10.4), so a supplied
* utility overrides the default it conflicts with and every default it does not conflict
* with survives. `tone` is the supported way to change a `Badge`'s appearance.
*/
function Badge({ tone = "neutral", className, ...properties }) {
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "badge",
		...properties,
		className: cn(badgeClassNames({ tone }), className)
	});
}
//#endregion
//#region src/components/separator.tsx
/**
* The classes a `Separator` carries (R10.5).
*
* ── The colour ──────────────────────────────────────────────────────────────
*
* `bg-border`, the one-pixel-rule role: `border` is the role that divides two regions
* of a single surface, which is exactly what a `Separator` does, and it is held to a
* lighter contrast bound than `input` for that reason (`src/tokens.css` records the
* ratios). It is a fill rather than a border because the rule is drawn by filling the
* element's box, not by bordering it — see the thickness note below.
*
* ── The thickness, and why it is a spacing step rather than one pixel ─────────
*
* shadcn draws the rule with `h-px` for a horizontal separator and `w-px` for a
* vertical one. Neither survives R10.5: `h-px` names `px`, a step of Tailwind's own
* scale, and this package's spacing scale has no one-pixel step to replace it with.
* The obvious alternative — a single-side border, `border-t` or `border-l`, over a
* zero-height box — fails the same rule for the same reason: `border-t` names `t`,
* which is neither a Design_Token nor a keyword, and `table.tsx` records the identical
* finding where it declines to draw a row rule. A full `border` passes the scan but
* boxes the element on all four sides, which is not a rule.
*
* So the rule is a filled bar at the smallest spacing step the scale offers,
* `hurdl-1`, drawn the long dimension of the orientation and the short dimension across
* it: `h-hurdl-1 w-full` lying down, `w-hurdl-1 h-full` standing up. It is thicker than
* shadcn's hairline, and deliberately so — the trade R10.5 asks for is a themeable
* measurement in place of a literal one, and a divider the theme can scale with the
* rest of its spacing is worth more here than a pixel the theme cannot reach. The
* `README.md` divergence note records the substitution.
*
* ── The rest ──────────────────────────────────────────────────────────────────
*
* `shrink-0`, so a `Separator` in a flex layout keeps its thickness rather than being
* squeezed to nothing by its neighbours — a flex-basis instruction and not a length,
* which is why the token scan admits it. The two orientation branches are
* `data-[orientation=…]:` variants over the primitive's own attribute, so one component
* dresses both and the base utility after each variant is token-valued.
*/
const SEPARATOR_CLASS_NAMES = [
	"shrink-0 bg-border",
	"data-[orientation=horizontal]:h-hurdl-1 data-[orientation=horizontal]:w-full",
	"data-[orientation=vertical]:w-hurdl-1 data-[orientation=vertical]:h-full"
].join(" ");
/**
* A thin rule dividing two regions of one surface (R10.1).
*
* ```tsx
* <Separator />
* <Separator orientation="vertical" />
* <Separator decorative={false} aria-label="Section break" />
* ```
*
* The element is Radix's separator primitive, which renders a `div` carrying
* `role="separator"` and `aria-orientation` — or, under `decorative` (the primitive's
* default), a `div` with no role at all, so a purely visual rule adds nothing to the
* accessibility tree. Which of the two it is, and which way it runs, are the primitive's
* decisions from the caller's `decorative` and `orientation`; nothing here reads either,
* which is invariant 8 of `AGENTS.md`.
*
* `data-slot="separator"` is shadcn's convention, unchanged, and is what lets a
* surrounding component reach the rule it places without either naming a class the other
* owns.
*
* `className` is composed through `cn` with the defaults first (R10.4), so a supplied
* utility — a different colour, a greater thickness — overrides the default it conflicts
* with and every default it does not conflict with survives.
*/
function Separator({ className, ...properties }) {
	return /* @__PURE__ */ jsx(Root$2, {
		"data-slot": "separator",
		...properties,
		className: cn(SEPARATOR_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/skeleton.tsx
/**
* The classes a `Skeleton` carries (R10.5).
*
* `bg-muted` is the subdued surface role — the dimmed fill a placeholder reads as "this
* will be content" rather than "this is content" — and `rounded-hurdl-medium` is the
* same corner `Input` and `Button` carry, so a `Skeleton` standing in for a field has
* the field's silhouette.
*
* No animation. shadcn applies `animate-pulse`, a Tailwind keyframe utility, and this
* package does not: a keyframe is motion that no Design_Token gauges and no
* reduced-motion rule reaches, so it is outside the token vocabulary R10.5 bounds this
* file to. The platform expresses motion as a `transition-*` keyed to a duration token
* instead, which the stylesheet rewrites to `0ms` under `prefers-reduced-motion`; a
* placeholder has no state to transition *between*, though — it appears, waits and is
* replaced — so there is nothing for a transition to animate, and a static dimmed block
* is what remains. It reads as a placeholder by its fill and its shape, which is enough,
* and it respects the reduced-motion preference by having no motion to suppress.
*/
const SKELETON_CLASS_NAMES = "bg-muted rounded-hurdl-medium";
/**
* A `div` standing in for content that has not loaded yet (R10.1).
*
* ```tsx
* <Skeleton className="h-hurdl-4 w-full" />
* <Skeleton className="size-hurdl-10 rounded-full" />
* <Stack gap="small">
*   <Skeleton className="h-hurdl-4 w-full" />
*   <Skeleton className="h-hurdl-4 w-full" />
* </Stack>
* ```
*
* The element carries no `role` and no accessible name of its own: a placeholder is a
* visual stand-in, and the region it fills is what should announce its loading state —
* a caller marks that region `aria-busy="true"` and removes it when the content
* arrives, so a screen reader is told the area is loading rather than reading a run of
* empty boxes. A `Skeleton` that announced itself would add noise to that announcement.
*
* `data-slot="skeleton"` is shadcn's convention, unchanged.
*
* `className` is composed through `cn` with the defaults first (R10.4), which is also
* how a caller gives the placeholder its size: the supplied `w-…` and `h-…` sit beside
* the fill and the radius rather than conflicting with them, so both survive.
*/
function Skeleton({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "skeleton",
		...properties,
		className: cn(SKELETON_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/breadcrumb.tsx
/**
* The classes {@link BreadcrumbList} carries (R10.5).
*
* The trail's layout and its resting colour, both declared here on the `ol` rather than
* repeated on every crumb: a wrapping flex row so a long trail folds onto a second line
* rather than overflowing, `gap-hurdl-2` between the crumbs and their separators, the
* caption size a secondary navigation reads at, and `text-muted-foreground` — the dimmed
* foreground, because the trail is context rather than the content, and the one crumb
* that is not dimmed is the current page, which says so by overriding the colour on
* {@link BreadcrumbPage}.
*/
const BREADCRUMB_LIST_CLASS_NAMES = [
	"flex flex-wrap items-center gap-hurdl-2",
	"font-hurdl-sans font-hurdl-regular text-hurdl-caption leading-hurdl-tight",
	"text-muted-foreground"
].join(" ");
/** The classes {@link BreadcrumbItem} carries: a crumb and its following separator on one baseline. */
const BREADCRUMB_ITEM_CLASS_NAMES = "inline-flex items-center gap-hurdl-2";
/**
* The classes {@link BreadcrumbLink} carries (R10.5).
*
* A link that darkens to the full foreground on hover — `hover:text-foreground` over the
* list's dimmed resting colour, a change of role rather than an opacity so the hover
* colour stays measured (R9.9) — faded through `transition-colors` on the medium
* duration token, which the stylesheet rewrites to `0ms` under reduced motion.
*
* `outline-none focus-visible:ring-2 focus-visible:ring-ring` is the platform's focus
* indicator, which a focusable crumb needs because a keyboard user tabs through the
* trail; `rounded-hurdl-small` is the corner the ring is drawn around.
*/
const BREADCRUMB_LINK_CLASS_NAMES = [
	"rounded-hurdl-small transition-colors duration-(--duration-hurdl-medium)",
	"hover:text-foreground",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring"
].join(" ");
/**
* The classes {@link BreadcrumbPage} carries: the current page, told from the trail by
* its colour and its weight.
*
* `text-foreground` lifts it out of the list's dimmed colour — this is where the user
* is, so it is the one crumb drawn at full strength — and `font-hurdl-regular` keeps it
* the trail's weight rather than emphasising it further, the colour alone being enough
* to mark the end of the path.
*/
const BREADCRUMB_PAGE_CLASS_NAMES = "font-hurdl-regular text-foreground";
/**
* A navigation trail (R10.1).
*
* ```tsx
* <Breadcrumb>
*   <BreadcrumbList>
*     <BreadcrumbItem>
*       <BreadcrumbLink href="/accounts">Accounts</BreadcrumbLink>
*     </BreadcrumbItem>
*     <BreadcrumbSeparator />
*     <BreadcrumbItem>
*       <BreadcrumbPage>Settings</BreadcrumbPage>
*     </BreadcrumbItem>
*   </BreadcrumbList>
* </Breadcrumb>
* ```
*
* A `nav` carrying `aria-label="breadcrumb"`, written before the caller's properties are
* spread so it is a default a caller may override rather than a fixture: the label is
* what tells assistive technology this `nav` is the breadcrumb trail rather than the
* primary navigation, and a page with its trail already named some other way passes its
* own `aria-label` and overrides it.
*
* `data-slot="breadcrumb"` is shadcn's convention, unchanged.
*/
function Breadcrumb({ className, ...properties }) {
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": "breadcrumb",
		"data-slot": "breadcrumb",
		...properties,
		className: cn(className)
	});
}
/** The ordered list of crumbs: an `ol`, carrying the trail's layout and colour (R10.1). */
function BreadcrumbList({ className, ...properties }) {
	return /* @__PURE__ */ jsx("ol", {
		"data-slot": "breadcrumb-list",
		...properties,
		className: cn(BREADCRUMB_LIST_CLASS_NAMES, className)
	});
}
/** One crumb: an `li` holding a link or the current-page mark, and its separator (R10.1). */
function BreadcrumbItem({ className, ...properties }) {
	return /* @__PURE__ */ jsx("li", {
		"data-slot": "breadcrumb-item",
		...properties,
		className: cn(BREADCRUMB_ITEM_CLASS_NAMES, className)
	});
}
/**
* A link to an ancestor of the current page (R10.1).
*
* An `a` by default, or the caller's own element under `asChild` — a router's `Link`,
* most often — through `Slot`, which merges this component's class and `data-slot` onto
* the child the caller supplies. The link activation, the keyboard handling and the
* destination are then the `a`'s or the slotted element's, never this component's, which
* is invariant 8 of `AGENTS.md`.
*/
function BreadcrumbLink({ asChild = false, className, ...properties }) {
	return /* @__PURE__ */ jsx(asChild ? Slot : "a", {
		"data-slot": "breadcrumb-link",
		...properties,
		className: cn(BREADCRUMB_LINK_CLASS_NAMES, className)
	});
}
/**
* The current page, as the last crumb (R10.1).
*
* A `span` carrying `role="link"`, `aria-disabled="true"` and `aria-current="page"`,
* which is shadcn's treatment and the accessible shape of a crumb that names where the
* user already is: it reads as a link so the trail is a run of links, but a disabled one
* that goes nowhere, and `aria-current="page"` is what a screen reader announces as the
* current location. The three are written before the caller's properties so each is a
* default a caller may override.
*/
function BreadcrumbPage({ className, ...properties }) {
	return /* @__PURE__ */ jsx("span", {
		role: "link",
		"aria-disabled": "true",
		"aria-current": "page",
		"data-slot": "breadcrumb-page",
		...properties,
		className: cn(BREADCRUMB_PAGE_CLASS_NAMES, className)
	});
}
/**
* The divider between two crumbs (R10.1).
*
* An `li` carrying `role="presentation"` and `aria-hidden="true"`, so the separator sits
* in the `ol` as markup requires but adds nothing to the accessibility tree — the trail
* a screen reader reads is the crumbs, not the slashes between them. Its content defaults
* to a `/`, and a caller passing children — an icon, a different glyph — replaces it; the
* separator is hidden either way, so the glyph is a visual choice with no accessible
* consequence.
*
* `data-slot="breadcrumb-separator"` is shadcn's convention, unchanged.
*/
function BreadcrumbSeparator({ children, className, ...properties }) {
	return /* @__PURE__ */ jsx("li", {
		role: "presentation",
		"aria-hidden": "true",
		"data-slot": "breadcrumb-separator",
		...properties,
		className: cn(className),
		children: children ?? "/"
	});
}
//#endregion
//#region src/components/scroll-area.tsx
/**
* The classes the viewport carries (R10.5).
*
* `size-full`, so the viewport fills the bounds the caller gave the root — the viewport
* is the element that actually scrolls, and it has to be the size of the area for its
* overflow to be the area's. shadcn adds `rounded-[inherit]` so the scrolled content
* clips to the root's corner; that is an arbitrary value R10.5 forbids, so the radius is
* stated as the token `rounded-hurdl-medium` instead — the same corner the root carries,
* so the clip matches without inheriting. `outline-none focus-visible:ring-2
* focus-visible:ring-ring` is the focus indicator a keyboard-scrollable viewport needs,
* which shadcn's current source also carries.
*/
const VIEWPORT_CLASS_NAMES = ["size-full rounded-hurdl-medium", "outline-none focus-visible:ring-2 focus-visible:ring-ring"].join(" ");
/** The classes the root carries: a positioned, clipping box with the matching corner. */
const ROOT_CLASS_NAMES = "relative overflow-hidden rounded-hurdl-medium";
/**
* The classes {@link ScrollBar} carries (R10.5).
*
* A flex track that neither takes pointer gestures meant for the content
* (`touch-none`) nor lets a drag select text (`select-none`), fading its appearance
* through `transition-colors` on the medium duration token so it arrives and leaves
* rather than snapping, which the stylesheet rewrites to `0ms` under reduced motion.
*
* The thickness is a spacing step rather than shadcn's `h-2.5`/`w-2.5`, which name
* steps of Tailwind's own scale R10.5 forbids: `w-hurdl-2` for the vertical bar's width
* and `h-hurdl-2` for the horizontal bar's height, each paired with `*-full` along the
* axis it runs, through the primitive's own `data-orientation`. The horizontal bar lays
* its thumb out in a column so the thumb fills the bar's height; the vertical bar needs
* no direction, a row being the default. `p-hurdl-1` insets the thumb from the track
* edge, as shadcn's does.
*/
const SCROLLBAR_CLASS_NAMES = [
	"flex touch-none select-none p-hurdl-1",
	"transition-colors duration-(--duration-hurdl-medium)",
	"data-[orientation=vertical]:h-full data-[orientation=vertical]:w-hurdl-2",
	"data-[orientation=horizontal]:w-full data-[orientation=horizontal]:h-hurdl-2",
	"data-[orientation=horizontal]:flex-col"
].join(" ");
/**
* The classes the thumb carries: the draggable handle.
*
* `bg-border` is the one-pixel-rule role, the quiet line colour a scrollbar thumb reads
* as against the content behind it, and `rounded-full` is the pill shadcn draws it as.
* `size-full` fills the track across its short axis; the long-axis extent is the portion
* of the content on screen, which the primitive sets as an inline style and which wins
* over the class, so the thumb grows and shrinks with the content as it must. shadcn
* writes `flex-1` for the cross-axis fill, a step of Tailwind's own scale this package
* does not use.
*/
const THUMB_CLASS_NAMES = "relative size-full rounded-full bg-border";
/**
* A sized box with styled, overlaying scrollbars (R10.1).
*
* ```tsx
* <ScrollArea className="h-hurdl-16 w-full">
*   <Stack gap="small">{rows}</Stack>
* </ScrollArea>
* ```
*
* The element is Radix's scroll-area root wrapping a viewport, a vertical scrollbar and
* the corner where two bars would meet, which is shadcn's composition. The scrollbars do
* not portal: the surface is the area itself, so there is nothing to lift out of a
* clipping ancestor the way `Select` and `Dialog` lift their surfaces, and the whole
* control renders in place.
*
* The viewport takes no `className` of its own: it is the inside of the control rather
* than part of its public surface, and a caller who could restyle it could break the
* agreement between its size and the root's that {@link VIEWPORT_CLASS_NAMES} records.
* The caller styles the area through the root's `className`.
*
* `data-slot="scroll-area"` and `data-slot="scroll-area-viewport"` are shadcn's
* convention, unchanged.
*
* `className` is composed through `cn` with the defaults first (R10.4), which is also
* how a caller gives the area its bounds.
*/
function ScrollArea({ className, children, ...properties }) {
	return /* @__PURE__ */ jsxs(Root$3, {
		"data-slot": "scroll-area",
		...properties,
		className: cn(ROOT_CLASS_NAMES, className),
		children: [
			/* @__PURE__ */ jsx(Viewport, {
				"data-slot": "scroll-area-viewport",
				className: VIEWPORT_CLASS_NAMES,
				children
			}),
			/* @__PURE__ */ jsx(ScrollBar, {}),
			/* @__PURE__ */ jsx(Corner, {})
		]
	});
}
/**
* One scrollbar of a {@link ScrollArea}, with its draggable thumb (R10.1).
*
* Rendered by {@link ScrollArea} for the vertical axis, and exported so a caller holding
* content that scrolls sideways can add a second with `orientation="horizontal"`, which
* is how shadcn splits the two. The thumb takes no `className`: its size along the scroll
* axis is the primitive's, set from the content, and its appearance is the inside of the
* control rather than part of its public surface.
*
* `data-slot="scroll-area-scrollbar"` and `data-slot="scroll-area-thumb"` are shadcn's
* convention, unchanged. `className` is composed through `cn` with the defaults first
* (R10.4).
*/
function ScrollBar({ className, orientation = "vertical", ...properties }) {
	return /* @__PURE__ */ jsx(ScrollAreaScrollbar, {
		"data-slot": "scroll-area-scrollbar",
		orientation,
		...properties,
		className: cn(SCROLLBAR_CLASS_NAMES, className),
		children: /* @__PURE__ */ jsx(ScrollAreaThumb, {
			"data-slot": "scroll-area-thumb",
			className: THUMB_CLASS_NAMES
		})
	});
}
//#endregion
//#region src/components/select.tsx
/**
* The classes the trigger carries (R10.5).
*
* Read against `input.tsx` rather than against shadcn alone, because a trigger and a
* field sit beside each other in a form and R18.2 asks the additions to be consistent
* with what is there. Everything the two share is stated identically: the border in
* `border-input`, the `bg-card text-card-foreground` pair, `rounded-hurdl-medium`,
* `px-hurdl-3 py-hurdl-2` with the height following from the padding, the
* `focus-visible:ring-2 focus-visible:ring-ring` indicator, the
* `aria-invalid:border-destructive` outline and the disabled treatment. shadcn's own
* trigger differs from its own input in each of those places; this one does not.
*
* Four departures from shadcn's source, beyond the measurements becoming tokens.
*
* `w-full` in place of `w-fit`. A trigger whose width is its content jumps as the
* chosen value changes, and a field in a form is as wide as the form: `Input` fills
* its container for the same reason, and the container decides the measure.
*
* `overflow-hidden` beside shadcn's `whitespace-nowrap`, in place of the
* `*:data-[slot=select-value]:line-clamp-1` rule it writes over the value. Both bound
* a long value to one line; the utility group `line-clamp-*` belongs to is one this
* package has no other use for, and clipping at the trigger's own edge is where the
* bound belongs.
*
* shadcn's `shadow-xs` is dropped. `Input` declares no elevation, and a field is not
* a raised surface — the elevation on a `Select` belongs to the surface that opens
* above the page, which is {@link SELECT_CONTENT_CLASS_NAMES}.
*
* `disabled:opacity-50` is the one value here that is not a token reference, there
* being no opacity Design_Token to reference; `button.tsx` carries the reasoning.
*
* `data-[placeholder]:text-muted-foreground` is shadcn's, unchanged, and it is the
* one place this component depends on the primitive's own attribute: the trigger
* carries `data-placeholder` exactly while no value has been chosen, so the
* placeholder is dimmed without this module reading the value.
*/
const SELECT_TRIGGER_CLASS_NAMES = [
	"flex w-full min-w-0 items-center justify-between gap-hurdl-2",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-tight",
	"rounded-hurdl-medium border border-input px-hurdl-3 py-hurdl-2",
	"bg-card text-card-foreground",
	"overflow-hidden whitespace-nowrap",
	"data-[placeholder]:text-muted-foreground",
	"transition-colors duration-(--duration-hurdl-medium)",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"aria-invalid:border-destructive",
	"disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
].join(" ");
/**
* The classes the disclosure indicator carries.
*
* The indicator itself is the primitive's: `SelectPrimitive.Icon` renders a
* `span` carrying `aria-hidden`, and — given no children — its own downward triangle
* character. That default is what this component uses, which is why no icon arrives
* with `Select` and why the Design_System still ships no icon set: an icon component
* would be an addition this feature's screens do not ask for, which R18.9 forbids,
* and the affordance a closed select needs is one glyph. `Alert` records the same
* absence from the other side, having dropped shadcn's reserved icon column.
*
* `text-hurdl-caption` because the glyph is a mark beside the value rather than part
* of it, and `text-muted-foreground` because it is the palette's dimmed foreground,
* which clears 4.5:1 on every surface (R9.9) and so stays perceivable while reading
* as secondary to the value. `shrink-0` keeps it from being squeezed out by a long
* value, which is the case `overflow-hidden` on the trigger would otherwise clip.
*/
const SELECT_ICON_CLASS_NAMES = "shrink-0 text-hurdl-caption text-muted-foreground";
/**
* The classes the open surface carries (R10.5).
*
* `bg-popover text-popover-foreground` is shadcn's, and the alias block in
* `src/tokens.css` resolves both onto the `card` role: this theme distinguishes a
* popover from a card by elevation rather than by fill, and records that
* approximation beside the alias. The elevation is therefore doing the work, and it
* is `shadow-hurdl-high` — the top step ADR 0004 added — where a `Card` carries
* `shadow-hurdl-low`, because this surface floats above the page rather than sitting
* on it. shadcn's `shadow-md` names a step of Tailwind's own scale.
*
* `border border-border` and `rounded-hurdl-medium` match the trigger the surface
* belongs to. `overflow-hidden` is what makes the radius clip the first and last
* item; the scrolling belongs to the viewport, which the primitive gives its own
* inline `overflow` rather than a class.
*
* Four groups of shadcn's classes are absent, and all four are absent for one
* reason: the primitive supplies what they express, or expresses it in a vocabulary
* R10.5 excludes.
*
* - `max-h-(--radix-select-content-available-height)`, `min-w-[8rem]` and
*   `origin-(--radix-select-content-transform-origin)` are the `popper` geometry.
*   {@link SelectContentProps} records why this component uses the primitive's
*   `item-aligned` position instead, which writes its own width, height and
*   minimum height as inline styles measured from the trigger.
* - the `data-[side=…]:slide-in-from-…` and `data-[state=…]:animate-…` classes are
*   `tailwindcss-animate`, which this package does not apply. An entrance animation
*   here would also have to honour the reduced-motion preference, and the token that
*   carries that is a duration on a transition rather than on a keyframe.
* - `z-50` is retained, and it is the one class in this file naming an integer rather
*   than a token: the surface is portalled to the end of the document, so it paints
*   above the page already, but a select opened inside another portalled surface
*   needs to say so. `test/token-literals.property.test.ts` records it in its
*   allow-list with that reason, a z-index being neither a colour, a length nor a
*   duration.
*/
const SELECT_CONTENT_CLASS_NAMES = [
	"relative z-50 overflow-hidden",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-tight",
	"rounded-hurdl-medium border border-border",
	"bg-popover text-popover-foreground shadow-hurdl-high"
].join(" ");
/**
* The classes the viewport carries: shadcn's `p-1` on the platform's spacing scale,
* so the first and last item are inset from the surface's own border rather than
* touching it.
*/
const SELECT_VIEWPORT_CLASS_NAMES = "p-hurdl-1";
/**
* The classes an item carries (R10.5).
*
* `focus:bg-accent focus:text-accent-foreground` is shadcn's and it is the highlight:
* the primitive moves DOM focus to the item under the pointer and to the item the
* arrow keys and the typeahead reach, so the highlighted item and the focused item
* are one thing and this component styles it once. `accent` is the palette's role for
* the hover surface of an otherwise unfilled control, which is exactly this.
*
* `data-[state=checked]:font-hurdl-semibold` is added. The primitive marks the chosen
* item `data-state="checked"`, and it is announced through `aria-selected`, so
* assistive technology is told either way — but a sighted reader has to be able to
* see which item is chosen by something other than a colour (WCAG 1.4.1), and the
* indicator {@link SELECT_ITEM_INDICATOR_CLASS_NAMES} carries sits at the far edge of
* the row. The weight states it on the label itself.
*
* `pr-hurdl-8` reserves the column the indicator is absolutely positioned in, which
* is shadcn's arrangement with its `pr-8` on the platform's scale. `rounded-hurdl-small`
* is shadcn's `rounded-sm`: the smallest radius, a step below the surface's own, so a
* highlighted row reads as inside the surface rather than as another surface.
*
* `outline-none` rather than shadcn's `outline-hidden`, matching `Input` and the
* trigger: the row is focused constantly as the pointer moves and the focus is
* conveyed by the highlight, so a ring around it would be a second indicator of one
* state. `cursor-default` is shadcn's — an item is not a link — and `select-none`
* keeps a drag across the list from selecting its text instead of choosing a value.
*/
const SELECT_ITEM_CLASS_NAMES = [
	"relative flex w-full cursor-default select-none items-center gap-hurdl-2",
	"rounded-hurdl-small py-hurdl-2 pr-hurdl-8 pl-hurdl-2",
	"outline-none focus:bg-accent focus:text-accent-foreground",
	"data-[state=checked]:font-hurdl-semibold",
	"data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
].join(" ");
/**
* The classes the chosen-value indicator carries.
*
* Absolutely positioned in the column `pr-hurdl-8` reserves, `right-hurdl-2` from the
* row's edge, which is shadcn's arrangement on the platform's spacing scale.
*
* shadcn puts these classes on a `span` *around* the primitive's indicator and sizes
* it `size-3.5` to hold an icon. Both go: the box is absolutely positioned, so it
* reserves no space whether it is rendered or not, and the glyph inside it sizes
* itself. The classes therefore sit on the primitive's own indicator, which renders a
* `span` exactly while the row is the chosen one.
*/
const SELECT_ITEM_INDICATOR_CLASS_NAMES = "absolute right-hurdl-2 flex items-center justify-center";
/**
* The character marking the chosen item.
*
* A character and not an icon, for the reason {@link SELECT_ICON_CLASS_NAMES}
* records: this package ships no icon set, and adding one is an addition R18.9 asks
* to be driven by a caller. shadcn renders `lucide-react`'s `CheckIcon` here; the
* primitive supplies its own default glyph for the trigger's indicator but none for
* this one, so the check mark is named here.
*
* It reaches no accessibility tree: `SelectPrimitive.ItemIndicator` renders a `span`
* carrying `aria-hidden`, and the chosen item is announced through `aria-selected`
* on the item itself.
*/
const CHOSEN_MARK = "✓";
/**
* A select: a control for choosing one value from a list (R18.1).
*
* ```tsx
* <Select defaultValue="fortnightly">
*   <SelectTrigger aria-label="Digest frequency">
*     <SelectValue placeholder="How often?" />
*   </SelectTrigger>
*   <SelectContent>
*     <SelectItem value="daily">Every day</SelectItem>
*     <SelectItem value="fortnightly">Every fortnight</SelectItem>
*     <SelectItem value="never" disabled>
*       Never
*     </SelectItem>
*   </SelectContent>
* </Select>
* ```
*
* Five names, and the composition is the primitive's. `Select` provides the state,
* `SelectTrigger` is the control the page shows, `SelectValue` renders the chosen
* item's text or the placeholder, `SelectContent` is the surface that opens, and
* `SelectItem` is one option. Each is load-bearing: a caller cannot express a select
* with fewer, and the arrangement is what lets a call site put its own content in a
* row without this package growing a property for every arrangement.
*
* Five and not shadcn's ten. `SelectGroup` and `SelectLabel` divide a long list into
* named sections, `SelectSeparator` draws the rule between them, and
* `SelectScrollUpButton` and `SelectScrollDownButton` are shadcn's chevron affordances
* on a list taller than the viewport. None is reached by a screen this feature adds,
* and R18.9 asks that an addition be driven by a caller rather than by anticipation,
* so none is exported — each would also need a fixture module of its own, which is
* five demonstrations of behaviour nothing yet asks for. The scroll buttons are
* absent from {@link SelectContent} as well as from the export list, which changes
* nothing about whether a long list scrolls: the primitive's viewport is scrollable
* of itself, and the buttons only add a second way to reach the same rows. Adding any
* of the five later is additive.
*
* Everything the control *does* is the primitive's and the user agent's: the popover,
* the arrow keys, `Home` and `End`, the typeahead that jumps to a row by its first
* characters, the dismissal, the focus management, the `role="combobox"` and
* `role="listbox"` pair, `aria-expanded`, `aria-activedescendant` and the hidden
* native `select` that carries the value into a form submission under the `name` the
* caller supplied (R18.6). Nothing here reads the value for any purpose and nothing
* here reimplements a keyboard handler, which is invariant 8 of `AGENTS.md` and the
* reason ADR 0003 chose this foundation.
*
* The accessible name is the caller's contribution, as it is for `Input` and
* `Switch`: it belongs on the trigger, either as an `aria-label` or as a `Label`
* pointing at it by `id`.
*
* No `data-slot` on this element, because there is no element: the primitive's root
* renders its children inside a provider and nothing of its own. Every other part
* carries shadcn's `data-slot` unchanged.
*/
function Select(properties) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Root, { ...properties });
}
/**
* The control a closed {@link Select} shows: a `button` carrying `role="combobox"`,
* its `aria-expanded` and the identifier of the surface it opens (R10.1, R10.3).
*
* The disclosure indicator is appended after the caller's children, as shadcn does,
* so a trigger holding a {@link SelectValue} and a trigger holding a value beside an
* icon of the consumer's own both end with the same glyph in the same place.
*
* `className` is composed through `cn` with the defaults first (R10.4). The indicator
* takes no `className`: it is part of the control's own presentation rather than of
* its public surface.
*/
function SelectTrigger({ className, children, ...properties }) {
	return /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
		"data-slot": "select-trigger",
		...properties,
		className: cn(SELECT_TRIGGER_CLASS_NAMES, className),
		children: [children, /* @__PURE__ */ jsx(SelectPrimitive.Icon, { className: SELECT_ICON_CLASS_NAMES })]
	});
}
/**
* The chosen item's text within a {@link SelectTrigger}, or the `placeholder` while
* nothing has been chosen (R10.1).
*
* No class of its own, as in shadcn's source. The primitive portals the chosen item's
* own text into this element, so it renders whatever the {@link SelectItem} rendered
* and takes its appearance from the trigger around it — including the dimmed
* placeholder, which the trigger's `data-placeholder` selects rather than this
* element.
*/
function SelectValue(properties) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Value, {
		"data-slot": "select-value",
		...properties
	});
}
/**
* The surface a {@link Select} opens: the list of items, portalled out of the page's
* own flow (R10.1).
*
* The portal is shadcn's and it is not optional. A select rendered inside a `Card`, a
* `Dialog` or anything with `overflow: hidden` would otherwise be clipped by its
* container, and the primitive positions the surface in the viewport's coordinates.
*
* `ThemeScope` is what the portal costs and what pays for it. Appending the surface
* near the end of the document takes it out from under the `ThemeSurface` whose class
* the theme's custom properties are declared under, so without it this surface
* resolved the light values inside a dark page — measured, at `rgb(255, 255, 255)`
* against a trigger of `rgb(17, 26, 43)`. It re-applies the theme and the mode the
* surface published and generates no box of its own; `src/theming/theme-scope.tsx`
* records the whole of it.
*
* `className` reaches the surface itself rather than the portal, the scope or the
* viewport (R10.4).
*/
function SelectContent({ className, children, ...properties }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx(ThemeScope, { children: /* @__PURE__ */ jsx(SelectPrimitive.Content, {
		"data-slot": "select-content",
		...properties,
		className: cn(SELECT_CONTENT_CLASS_NAMES, className),
		children: /* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
			className: SELECT_VIEWPORT_CLASS_NAMES,
			children
		})
	}) }) });
}
/**
* One option within a {@link SelectContent}: a row carrying `role="option"`, its
* `aria-selected` and the value choosing it sets (R10.1, R10.3).
*
* `disabled` reaches the primitive, which then skips the row for the arrow keys, the
* typeahead and the pointer, so a disabled option cannot be chosen by any route.
*
* The children are rendered inside the primitive's item text, which is what the
* trigger's {@link SelectValue} shows once the row is chosen — so a row's label is
* written once and appears in both places. The indicator is rendered before it, as
* shadcn does, because it is absolutely positioned and its order in the markup
* decides nothing.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function SelectItem({ className, children, ...properties }) {
	return /* @__PURE__ */ jsxs(SelectPrimitive.Item, {
		"data-slot": "select-item",
		...properties,
		className: cn(SELECT_ITEM_CLASS_NAMES, className),
		children: [/* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, {
			className: SELECT_ITEM_INDICATOR_CLASS_NAMES,
			children: CHOSEN_MARK
		}), /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })]
	});
}
//#endregion
//#region src/components/dialog.tsx
/**
* The classes the scrim carries (R10.5).
*
* `fixed inset-0` is shadcn's, and it is what makes the scrim the viewport: the
* surface is portalled to the end of the document, so there is no ancestor whose box
* it could fill. `inset-0` is one of the classes naming a bare number rather than a
* Design_Token, because there is no zero step on the spacing scale to name instead;
* `test/token-literals.property.test.ts` records it in its allow-list with that
* reason, beside `min-w-0` and `border-0`, which are there for the same one. `z-50` is
* shadcn's too and is the stacking order `Select`'s portalled surface already needed.
*
* The fill is the one place this component departs from shadcn's colour vocabulary
* rather than resolving it, and it is a departure because the vocabulary has nothing
* to resolve. shadcn writes `bg-black/50`: a literal colour, and R9.2 fixes this
* palette at fifteen semantic backgrounds with no scrim among them, so there is no
* token to reference and adding one is a change to the palette rather than to this
* component. What is available is the page's own surface, which is what is used:
* `bg-background` at `opacity-50`, so the content behind fades halfway towards the
* theme's own base — lighter in the light theme, darker in the dark one — rather than
* towards a black the palette never declared. The polarity is therefore the theme's in
* both modes, and the dialog above it is told apart from the faded page by the same
* means a `Card` is told apart from the page it sits on: a different surface role, a
* border, and elevation. A product wanting shadcn's darkening scrim in both modes
* declares a scrim role in its own theme; that is a palette addition, and it is
* recorded here as the thing this component would reference if it existed.
*
* `opacity-50` is the second class here naming a bare number, and opacity is not a
* Design_Token family; `button.tsx` carries the reasoning where the disabled state
* uses the same value.
*
* shadcn's `data-[state=…]:animate-…` classes are absent, as they are from
* `SelectContent`: they are `tailwindcss-animate`, which this package does not apply,
* and a fade here would have to honour the reduced-motion preference, which this
* package carries as a duration on a transition rather than on a keyframe.
*/
const DIALOG_OVERLAY_CLASS_NAMES = ["fixed inset-0 z-50", "bg-background opacity-50"].join(" ");
/**
* The classes the dialog surface carries, by size (R10.5).
*
* `class-variance-authority`, so the size-independent base is declared once and each
* size contributes only how it is measured. The base is read against `card.tsx` rather
* than shadcn alone, because both are raised surfaces holding a title, a body and a row
* of controls, and R18.2 asks the additions to be consistent with what is there:
* `bg-card text-card-foreground`, `border border-border`, `rounded-hurdl-large`, the
* flex column with `gap-hurdl-4`, the prose typography, and `p-hurdl-6` — `Card`'s
* largest padding step, shadcn's `p-6`. The elevation differs and that is the point:
* `shadow-hurdl-high`, the top step ADR 0004 added, where a `Card` carries
* `shadow-hurdl-low`, because this surface floats above the page; `src/tokens.css`
* records that this theme separates a floating surface from a resting one by elevation.
*
* The base positions the surface and the sizes measure it. `fixed inset-hurdl-4` makes
* the containing box the viewport inset by one spacing step, `m-auto` centres the
* surface in that box on both axes, `max-h-full` keeps it from growing past the box, and
* `overflow-y-auto` scrolls its own content inside it while the page behind stays locked
* by the primitive. `inset-hurdl-4` is the gutter shadcn writes as the arbitrary
* `max-w-[calc(100%-2rem)]`, expressed as a Design_Token so a theme changing the spacing
* scale moves it.
*
* `default` adds `h-fit w-fit`: the surface is as wide and tall as its content within
* the gutter, which is the rule `Card` keeps and the behaviour this dialog always had.
* `large` adds `h-full w-full`: the surface fills the inset box, so the content inside —
* a document, an `iframe` — has the whole gutter. No maximum width is declared for
* either, because `fit-content` is already bounded by the box and `full` is the box; a
* caller wanting a narrower measure states it on what it puts inside, the one place that
* knows what the content is.
*/
const dialogContentClassNames = cva([
	"fixed inset-hurdl-4 z-50 m-auto max-h-full overflow-y-auto",
	"flex flex-col gap-hurdl-4",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-relaxed",
	"rounded-hurdl-large border border-border p-hurdl-6",
	"bg-card text-card-foreground shadow-hurdl-high",
	"hurdl-animate-zoom-in"
].join(" "), {
	variants: { size: {
		default: "h-fit w-fit",
		large: "h-full w-full"
	} },
	defaultVariants: { size: "default" }
});
/**
* The classes {@link DialogHeader} carries: `CardHeader`'s own, which is shadcn's
* dialog header less the parts this package has no use for.
*
* shadcn writes `flex flex-col gap-2 text-center sm:text-left`. The alignment pair is
* dropped: a title centred on a narrow viewport and leading-aligned on a wide one is a
* responsive decision, and this package declares no breakpoint anywhere, so the header
* reads in the writing direction at every width. What remains is what a header is for,
* and it is stated with the same spacing step `CardHeader` uses, because a title and a
* description stacked is the same arrangement in both.
*/
const DIALOG_HEADER_CLASS_NAMES = "flex flex-col gap-hurdl-1";
/**
* The classes {@link DialogTitle} carries.
*
* shadcn writes `text-lg leading-none font-semibold`, and this is the one place the
* dialog states a heading level of appearance that a `Card` does not:
* `text-hurdl-subheading` where `CardTitle` declares no size at all. The reason is the
* element. A `CardTitle` is a `div`, because a `Card` cannot know which heading level
* would be correct in a page it has never seen; a `DialogTitle` is the primitive's
* `h2` inside a region carrying `role="dialog"`, with the rest of the document hidden
* while it is open, which is a context of its own rather than a position in the page's
* outline, so it is both a heading and the surface's own largest text.
*
* `leading-hurdl-tight` is the tightest line height, as `CardTitle` uses, and no
* colour: the title takes the surface's foreground, so a dialog's colours are declared
* in one place.
*/
const DIALOG_TITLE_CLASS_NAMES = "font-hurdl-semibold text-hurdl-subheading leading-hurdl-tight";
/**
* The classes {@link DialogDescription} carries: `CardDescription`'s own, which is
* shadcn's `text-muted-foreground text-sm` on the platform's scales.
*
* The caption size and the dimmed foreground together, which is what distinguishes
* supporting prose from the dialog's own content. `muted-foreground` is a measured
* colour rather than an approximation — ADR 0004's palette gives it its own value,
* clearing 4.5:1 on every surface — so the dimming is real and the size is not
* carrying it alone.
*/
const DIALOG_DESCRIPTION_CLASS_NAMES = ["text-hurdl-caption leading-hurdl-relaxed", "text-muted-foreground"].join(" ");
/**
* The classes {@link DialogFooter} carries.
*
* `CardFooter`'s row and its gap, plus `justify-end`, which is the one departure.
* shadcn writes `flex flex-col-reverse gap-2 sm:flex-row sm:justify-end`: the same
* trailing alignment, arrived at through a breakpoint and a reversal this package has
* no breakpoint to express. The alignment is kept because a dialog's footer is where
* the decision is taken — the confirming control sits at the end of the row, after the
* one that dismisses — and a caller that wants another arrangement composes a `Stack`
* inside it.
*/
const DIALOG_FOOTER_CLASS_NAMES = "flex items-center justify-end gap-hurdl-2";
/**
* A modal dialog: a surface that opens over the page and holds the whole of the
* interaction until it is dismissed (R18.1).
*
* ```tsx
* <Dialog>
*   <DialogTrigger asChild>
*     <Button variant="destructive">Close account</Button>
*   </DialogTrigger>
*   <DialogContent>
*     <DialogHeader>
*       <DialogTitle>Close this account?</DialogTitle>
*       <DialogDescription>
*         Your sessions end immediately and your address stays reserved.
*       </DialogDescription>
*     </DialogHeader>
*     <DialogFooter>
*       <Button variant="secondary" onClick={keepAccount}>
*         Keep it
*       </Button>
*       <Button variant="destructive" onClick={closeAccount}>
*         Close it
*       </Button>
*     </DialogFooter>
*   </DialogContent>
* </Dialog>
* ```
*
* Seven names, and the composition is shadcn's. `Dialog` provides the state,
* `DialogTrigger` is the control that opens it, `DialogContent` is the surface,
* `DialogHeader` stacks the two parts that name and describe it, `DialogTitle` is the
* accessible name, `DialogDescription` the accessible description, and `DialogFooter`
* the row the decision is taken in. `card.tsx` and `alert.tsx` already compose this
* way, which is what R18.2 asks for.
*
* Seven and not shadcn's nine. `DialogClose` and `DialogOverlay` are absent from the
* export list, and each for its own reason.
*
* The overlay is absent because it is not a decision a caller makes: it is the scrim,
* every modal dialog has exactly one, and {@link DialogContent} renders it. Exporting
* it would offer a caller the chance to render a dialog with no scrim, or two.
*
* `DialogClose` is absent because R18.9 asks that an addition be driven by a caller,
* and the screens this feature adds close a dialog by deciding something: a control in
* the footer runs the act and the caller's `onOpenChange` follows it, or the person
* dismisses instead. Dismissal has three routes without it — the escape key, a press
* outside the surface, and the caller's own control — and the first two are the
* primitive's. A `DialogClose` later is additive, and it would need a fixture module of
* its own.
*
* There is no close affordance drawn inside the surface either. shadcn renders one in
* the corner, as `lucide-react`'s `XIcon` with a visually hidden "Close": this package
* ships no icon set, for the reason `select.tsx` records, and a label rendered here
* would be the first user-facing word the Design_System supplied on its own behalf,
* which is a decision about language rather than about appearance. A caller wanting a
* visible dismissal puts a `Button` in the footer, where its wording is the product's.
*
* Everything the dialog *does* is the primitive's and the user agent's: the focus trap
* while it is open, the return of focus to the element that opened it when it closes,
* the dismissal on the escape key and on a press outside the surface, the scroll lock
* on the page behind, the `role="dialog"`, the hiding of the rest of the document from
* assistive technology while it is open, and the `aria-labelledby` and
* `aria-describedby` wired to the {@link DialogTitle} and the
* {@link DialogDescription} rendered inside it (R18.5). None of that is reimplemented
* here, which is invariant 8 of `AGENTS.md` and the reason ADR 0003 chose this
* foundation.
*
* A `DialogContent` holding no `DialogTitle` is therefore a call site with no
* accessible name, and nothing says so: the primitive points `aria-labelledby` at the
* title exactly when one is rendered and omits the attribute otherwise, so a dialog
* that named itself by its content alone would announce nothing on opening and fail no
* check here. A dialog names itself from its title, so the title is not optional
* decoration. The description is optional, and omitting it omits `aria-describedby`
* with it.
*
* No `data-slot` on this element, because there is no element: the primitive's root
* renders its children inside a provider and nothing of its own, which is what
* `select.tsx` records of its own root. Every other part carries shadcn's `data-slot`
* unchanged.
*/
function Dialog(properties) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, { ...properties });
}
/**
* The control that opens a {@link Dialog}: a `button` carrying `aria-haspopup`, its
* `aria-expanded` and the identifier of the surface it opens (R10.1, R10.3).
*
* No class of its own, as in shadcn's source, and that is what makes the trigger a
* behaviour rather than an appearance: a call site wraps the control it already wanted
* — usually a `Button` — with `asChild`, and the trigger contributes the press
* handling, the state attributes and the ARIA wiring to it. `asChild` is the
* primitive's own property rather than one added here, as `SelectTrigger`'s is.
*
* The element the primitive is also the element focus returns to when the dialog
* closes, which is why a `Dialog` opened from a control that unmounts while it is open
* has nowhere to return focus to; a caller in that position states
* `onCloseAutoFocus` on the content.
*/
function DialogTrigger(properties) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Trigger, {
		"data-slot": "dialog-trigger",
		...properties
	});
}
/**
* The surface a {@link Dialog} opens: the scrim, and the modal region above it,
* portalled out of the page's own flow (R10.1).
*
* The portal is shadcn's and it is not optional, for the reason `SelectContent`
* records: a dialog rendered inside a `Card` or anything carrying `overflow: hidden`
* would otherwise be clipped by its container, and both the scrim and the surface are
* positioned in the viewport's own coordinates.
*
* The scrim is rendered before the surface, so the surface paints above it, and it
* takes no `className`: it is part of what a modal dialog *is* rather than part of this
* component's public surface, and a caller who could restyle it could remove the one
* indication that the page behind is inert.
*
* `ThemeScope` wraps both, and it is what makes the theme survive the portal. The
* scrim and the surface are appended near the end of the document, out from under the
* `ThemeSurface` whose class the theme's custom properties are declared under, so
* without it a dialog opened over a dark page drew the *light* page colour across the
* viewport — measured, at `rgb(248, 250, 252)` over a page of `rgb(11, 18, 32)`, which
* is a scrim that brightens what it is meant to fade. It re-applies the theme and the
* mode the surface published and generates no box of its own, so both children stay
* positioned in the viewport's coordinates; `src/theming/theme-scope.tsx` records the
* whole of it.
*
* One scope around the pair rather than one each, because the primitive portals each
* child it is given separately: one child is one portal, and the scrim and the surface
* stay adjacent in the document as they were.
*
* `className` reaches the surface itself rather than the portal, the scope or the scrim
* (R10.4), composed through `cn` with the defaults first, so a supplied utility
* overrides the default it conflicts with and every default it does not conflict with
* survives.
*/
function DialogContent({ size = "default", className, children, ...properties }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Portal, { children: /* @__PURE__ */ jsxs(ThemeScope, { children: [/* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
		"data-slot": "dialog-overlay",
		className: DIALOG_OVERLAY_CLASS_NAMES
	}), /* @__PURE__ */ jsx(DialogPrimitive.Content, {
		"data-slot": "dialog-content",
		...properties,
		className: cn(dialogContentClassNames({ size }), className),
		children
	})] }) });
}
/** The title and description of a {@link Dialog}, stacked (R10.1). */
function DialogHeader({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "dialog-header",
		...properties,
		className: cn(DIALOG_HEADER_CLASS_NAMES, className)
	});
}
/**
* What a {@link Dialog} is about: the primitive's `h2`, and the dialog's accessible
* name (R10.1).
*
* The primitive points the surface's `aria-labelledby` at this element, so the text
* here is what assistive technology announces when the dialog opens. That is why it is
* a heading rather than a `div` as `CardTitle` is: a dialog is a context of its own
* rather than a position in the page's outline, so an `h2` inside it adds no level to
* the page's heading order.
*/
function DialogTitle({ className, ...properties }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Title, {
		"data-slot": "dialog-title",
		...properties,
		className: cn(DIALOG_TITLE_CLASS_NAMES, className)
	});
}
/**
* Supporting prose beneath a {@link DialogTitle}, and the dialog's accessible
* description (R10.1).
*
* The primitive points the surface's `aria-describedby` at this element, so this is
* where the consequence of the decision belongs: it is read after the name and before
* the controls. A dialog with nothing to describe omits it, and the primitive omits the
* attribute with it.
*/
function DialogDescription({ className, ...properties }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Description, {
		"data-slot": "dialog-description",
		...properties,
		className: cn(DIALOG_DESCRIPTION_CLASS_NAMES, className)
	});
}
/** The controls a {@link Dialog} ends with, in a row aligned to its trailing edge (R10.1). */
function DialogFooter({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "dialog-footer",
		...properties,
		className: cn(DIALOG_FOOTER_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/combobox.tsx
const ComboboxContext = createContext(null);
/** The context, or a thrown error naming the part that was rendered outside a combobox. */
function useComboboxContext(part) {
	const context = useContext(ComboboxContext);
	if (context === null) throw new Error(`${part} must be rendered within a Combobox.`);
	return context;
}
/** The identifier of the row standing for a value, derived so the input and the row agree. */
function optionId(baseId, value) {
	return `${baseId}-option-${value}`;
}
/**
* The classes the input carries (R10.5).
*
* Stated identically to `Input` and to `SelectTrigger`, because the three sit beside one
* another in a form and R18.2 asks the additions to be consistent with what is there:
* the `border-input` boundary, the `bg-card text-card-foreground` pair,
* `rounded-hurdl-medium`, `px-hurdl-3 py-hurdl-2` with the height following from the
* padding, the `placeholder:text-muted-foreground` placeholder, the
* `focus-visible:ring-2 focus-visible:ring-ring` indicator, the
* `aria-invalid:border-destructive` outline and the disabled treatment. The one value
* that is not a token reference is `disabled:opacity-50`, there being no opacity
* Design_Token; `button.tsx` carries the reasoning.
*/
const COMBOBOX_INPUT_CLASS_NAMES = [
	"flex w-full min-w-0",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-tight",
	"rounded-hurdl-medium border border-input px-hurdl-3 py-hurdl-2",
	"bg-card text-card-foreground",
	"placeholder:text-muted-foreground",
	"selection:bg-primary selection:text-primary-foreground",
	"transition-colors duration-(--duration-hurdl-medium)",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"aria-invalid:border-destructive",
	"disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
].join(" ");
/**
* The classes the open surface carries (R10.5).
*
* Stated identically to `SelectContent`, because the two are the same thing — a list
* floating above the page, told from a card by its elevation — so a combobox's list and
* a select's read alike: `bg-popover text-popover-foreground` resolved onto the card
* role, `shadow-hurdl-high` the top elevation step, `border border-border` and
* `rounded-hurdl-medium` matching the field it belongs to, and `overflow-hidden`
* clipping the first and last row to the radius.
*
* `z-50` is the one class naming an integer rather than a token, and it is here for the
* reason it is on `SelectContent`: the surface is portalled to the end of the document,
* so it paints above the page, but a combobox opened inside another portalled surface
* needs to say so. `test/token-literals.property.test.ts` records it in its allow-list.
*
* No width is declared. The surface takes the width of the field it is anchored to —
* `--radix-popover-trigger-width`, which the primitive publishes — applied as a style on
* {@link ComboboxContent} rather than as a class, because a reference to that custom
* property is outside the Design_Token vocabulary R10.5 bounds this file to, exactly as
* `SelectContentProps` records of the popper geometry it declines.
*/
const COMBOBOX_CONTENT_CLASS_NAMES = [
	"relative z-50 overflow-hidden",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-tight",
	"rounded-hurdl-medium border border-border",
	"bg-popover text-popover-foreground shadow-hurdl-high"
].join(" ");
/** The classes the viewport carries: `SelectContent`'s own inset, so rows clear the border. */
const COMBOBOX_VIEWPORT_CLASS_NAMES = "p-hurdl-1";
/**
* The classes a row carries (R10.5).
*
* Stated against `SelectItem`: `focus:bg-accent focus:text-accent-foreground` is the
* highlight, `data-[highlighted]` driving it from this component's active cursor rather
* than from DOM focus — the input keeps focus, so the row's highlight is a
* data-attribute the arrow keys move. `rounded-hurdl-small` is a step below the surface,
* so a highlighted row reads as inside it; `data-[state=checked]:font-hurdl-semibold`
* marks the chosen row by weight as well as colour (WCAG 1.4.1); and the disabled
* treatment matches `SelectItem`'s. `cursor-default` because a row is not a link and
* `select-none` so a drag chooses rather than selects text.
*/
const COMBOBOX_ITEM_CLASS_NAMES = [
	"relative flex w-full cursor-default select-none items-center gap-hurdl-2",
	"rounded-hurdl-small py-hurdl-2 px-hurdl-2",
	"outline-none",
	"data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
	"data-[state=checked]:font-hurdl-semibold",
	"data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
].join(" ");
/**
* The classes the empty row carries: the dimmed foreground on the same inset a row sits
* at, so the "no matches" line reads as subdued text within the list rather than as a
* row that could be chosen.
*/
const COMBOBOX_EMPTY_CLASS_NAMES = [
	"flex w-full items-center gap-hurdl-2",
	"py-hurdl-2 px-hurdl-2",
	"text-hurdl-body text-muted-foreground"
].join(" ");
/**
* A combobox: a text field that filters a list of options and sets a value when one is
* chosen (R18.1).
*
* ```tsx
* <Combobox options={categories} value={category} onValueChange={setCategory}>
*   <ComboboxInput aria-label="Category" placeholder="Type to filter…" />
*   <ComboboxContent>
*     {(matches) =>
*       matches.length === 0 ? (
*         <ComboboxEmpty>No matching category</ComboboxEmpty>
*       ) : (
*         matches.map((option) => (
*           <ComboboxItem key={option.value} option={option} />
*         ))
*       )
*     }
*   </ComboboxContent>
* </Combobox>
* ```
*
* Five names, and the composition is the shape `Select` and `Dialog` take. `Combobox`
* holds the state and the filter, `ComboboxInput` is the field the user types into,
* `ComboboxContent` is the surface that opens, `ComboboxItem` is one filtered row, and
* `ComboboxEmpty` is the line shown when nothing matches. A caller cannot express "type
* to filter, pick one" with fewer: the input and the content are both load-bearing, the
* item is how a row is rendered, and the empty line is the state a filter that excluded
* everything leaves behind.
*
* The accessible name is the caller's contribution, as it is for `Input` and `Select`:
* it belongs on {@link ComboboxInput}, either as an `aria-label` or as a `Label`
* pointing at it by `id`.
*
* No `data-slot` on this element: like `Select`'s and `Dialog`'s roots it renders no
* element of its own, only the popover primitive's root around a context provider. Every
* other part carries a `data-slot`.
*/
function Combobox({ options, value, onValueChange, inputValue, onInputValueChange, open, onOpenChange, children }) {
	const baseId = useId();
	const listboxId = `${baseId}-listbox`;
	const [internalInputValue, setInternalInputValue] = useState("");
	const [internalValue, setInternalValue] = useState(void 0);
	const [internalOpen, setInternalOpen] = useState(false);
	const [activeValue, setActiveValue] = useState(void 0);
	const resolvedInputValue = inputValue ?? internalInputValue;
	const resolvedValue = value ?? internalValue;
	const resolvedOpen = open ?? internalOpen;
	/**
	* The rows matching the query, derived rather than stored, so the list cannot drift
	* from the field. A case-insensitive substring test, which is the filter the admin
	* panel's category picker asks for; a caller wanting another match narrows `options`
	* itself, and this then matches an already-narrowed list.
	*/
	const matches = useMemo(() => {
		const query = resolvedInputValue.trim().toLowerCase();
		if (query === "") return options;
		return options.filter((option) => option.label.toLowerCase().includes(query));
	}, [options, resolvedInputValue]);
	const setInputValue = (next) => {
		if (inputValue === void 0) setInternalInputValue(next);
		onInputValueChange?.(next);
	};
	const setOpen = (next) => {
		if (open === void 0) setInternalOpen(next);
		onOpenChange?.(next);
	};
	const choose = (next) => {
		if (value === void 0) setInternalValue(next);
		onValueChange?.(next);
		const chosen = options.find((option) => option.value === next);
		if (chosen !== void 0) setInputValue(chosen.label);
		setOpen(false);
	};
	const contextValue = useMemo(() => ({
		inputValue: resolvedInputValue,
		setInputValue,
		value: resolvedValue,
		choose,
		open: resolvedOpen,
		setOpen,
		matches,
		activeValue,
		setActiveValue,
		baseId,
		listboxId
	}), [
		resolvedInputValue,
		resolvedValue,
		resolvedOpen,
		matches,
		activeValue,
		baseId,
		listboxId
	]);
	return /* @__PURE__ */ jsx(ComboboxContext.Provider, {
		value: contextValue,
		children: /* @__PURE__ */ jsx(PopoverPrimitive.Root, {
			open: resolvedOpen,
			onOpenChange: setOpen,
			children
		})
	});
}
/**
* The field a {@link Combobox} is typed into: a native `input` carrying `role="combobox"`,
* its `aria-expanded`, the identifier of the surface it controls and the active row's
* identifier in `aria-activedescendant` (R10.1, R10.3).
*
* Wrapped in the popover's `Anchor` so the surface opens against the field and takes its
* width. The input keeps DOM focus the whole time the list is open, which is the ARIA
* combobox pattern: the arrow keys move the active cursor through `aria-activedescendant`
* rather than moving focus onto a row, and `Enter` chooses the active row. Typing filters
* the list and opens it; `ArrowDown` opens it; and the escape key and a press outside are
* the primitive's dismissal, so neither is handled here.
*
* `className` composes through `cn` with the defaults first (R10.4).
*/
function ComboboxInput({ invalid = false, className, onKeyDown, onChange, disabled, ...properties }) {
	const { inputValue, setInputValue, open, setOpen, matches, activeValue, setActiveValue, choose, baseId, listboxId } = useComboboxContext("ComboboxInput");
	const listboxShown = open && matches.length > 0;
	const reachable = matches.filter((option) => option.disabled !== true);
	const activeIndex = reachable.findIndex((option) => option.value === activeValue);
	const moveActive = (delta) => {
		if (reachable.length === 0) return;
		const next = ((activeIndex === -1 ? delta > 0 ? -1 : 0 : activeIndex) + delta + reachable.length) % reachable.length;
		setActiveValue(reachable[next]?.value);
	};
	const handleKeyDown = (event) => {
		onKeyDown?.(event);
		if (event.defaultPrevented) return;
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (!open) setOpen(true);
				moveActive(1);
				return;
			case "ArrowUp":
				event.preventDefault();
				if (!open) setOpen(true);
				moveActive(-1);
				return;
			case "Enter":
				if (open && activeValue !== void 0) {
					event.preventDefault();
					choose(activeValue);
				}
				return;
			default: return;
		}
	};
	return /* @__PURE__ */ jsx(PopoverPrimitive.Anchor, {
		asChild: true,
		children: /* @__PURE__ */ jsx("input", {
			"data-slot": "combobox-input",
			role: "combobox",
			"aria-autocomplete": "list",
			"aria-expanded": listboxShown,
			"aria-controls": listboxShown ? listboxId : void 0,
			"aria-activedescendant": listboxShown && activeValue !== void 0 ? optionId(baseId, activeValue) : void 0,
			"aria-invalid": invalid ? true : void 0,
			autoComplete: "off",
			disabled,
			value: inputValue,
			...properties,
			onChange: (event) => {
				onChange?.(event);
				setInputValue(event.target.value);
				if (!open) setOpen(true);
				setActiveValue(void 0);
			},
			onKeyDown: handleKeyDown,
			className: cn(COMBOBOX_INPUT_CLASS_NAMES, className)
		})
	});
}
/**
* The surface a {@link Combobox} opens: the filtered list, portalled out of the page's
* flow (R10.1).
*
* The portal is the primitive's and it is not optional, for the reason `SelectContent`
* records: a combobox inside a `Card` or anything carrying `overflow: hidden` would
* otherwise be clipped. `ThemeScope` wraps the content so the theme survives the portal —
* invariant 10, and the defect `theme-scope.tsx` records — re-applying the theme and the
* mode the surface published.
*
* `onOpenAutoFocus` is prevented, which is the one departure the pattern requires: the
* primitive would move focus into the surface when it opens, but a combobox keeps focus
* on the field so typing continues to filter, so the content takes no focus and the
* input keeps it.
*
* The children are a render prop given the current matches, so a caller renders a
* {@link ComboboxEmpty} when nothing matches and a {@link ComboboxItem} per row
* otherwise — the one place a call site decides how a filtered list reads. A plain node
* is also accepted, for a caller that renders the rows itself.
*
* The listbox carries `role="listbox"` and the identifier the input points at, and
* `style` sets its width to the field's through the custom property the primitive
* publishes. `className` reaches the surface rather than the portal, the scope or the
* listbox (R10.4).
*/
function ComboboxContent({ className, children, style, onOpenAutoFocus, ...properties }) {
	const { matches, listboxId } = useComboboxContext("ComboboxContent");
	const populated = matches.length > 0;
	return /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(ThemeScope, { children: /* @__PURE__ */ jsx(PopoverPrimitive.Content, {
		"data-slot": "combobox-content",
		role: "presentation",
		onOpenAutoFocus: (event) => {
			onOpenAutoFocus?.(event);
			event.preventDefault();
		},
		style: {
			width: "var(--radix-popover-trigger-width)",
			...style
		},
		...properties,
		className: cn(COMBOBOX_CONTENT_CLASS_NAMES, className),
		children: /* @__PURE__ */ jsx("div", {
			"data-slot": "combobox-listbox",
			role: populated ? "listbox" : "presentation",
			id: listboxId,
			className: COMBOBOX_VIEWPORT_CLASS_NAMES,
			children: typeof children === "function" ? children(matches) : children
		})
	}) }) });
}
/**
* One filtered row within a {@link ComboboxContent}: a `div` carrying `role="option"`,
* its `aria-selected` and the value choosing it sets (R10.1, R10.3).
*
* The row is chosen on a pointer press and highlighted while it is the active cursor the
* input's arrow keys reach — `data-highlighted` driving the fill, because DOM focus
* stays on the input and cannot carry the highlight here as it does in a `Select`. A
* disabled option carries `data-disabled` and `aria-disabled`, cannot be chosen by a
* press, and is skipped by the arrow keys.
*
* The children default to the option's own label, so a caller writes a row's label once
* on the option and may override the rendering per row. `className` composes through `cn`
* with the defaults first (R10.4).
*/
function ComboboxItem({ option, children, className, onPointerEnter, onClick, ...properties }) {
	const { value, choose, activeValue, setActiveValue, baseId } = useComboboxContext("ComboboxItem");
	const disabled = option.disabled === true;
	const chosen = value === option.value;
	const highlighted = activeValue === option.value;
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "combobox-item",
		role: "option",
		id: optionId(baseId, option.value),
		"aria-selected": chosen,
		"aria-disabled": disabled ? true : void 0,
		"data-state": chosen ? "checked" : "unchecked",
		"data-highlighted": highlighted ? "" : void 0,
		"data-disabled": disabled ? "" : void 0,
		onPointerEnter: (event) => {
			onPointerEnter?.(event);
			if (!disabled) setActiveValue(option.value);
		},
		onClick: (event) => {
			onClick?.(event);
			if (!disabled) choose(option.value);
		},
		...properties,
		className: cn(COMBOBOX_ITEM_CLASS_NAMES, className),
		children: children ?? option.label
	});
}
/**
* The line a {@link ComboboxContent} shows when the filter matches nothing: a `div`
* carrying the dimmed foreground, so a filtered-to-empty list says so rather than
* appearing broken (R10.1).
*
* It carries no `role`, so it adds no option to the listbox the input announces a count
* for: an empty state is a message rather than a choice. A caller renders it in place of
* the rows when the matches are empty, which {@link ComboboxContent}'s render prop makes
* the natural shape.
*
* `className` composes through `cn` with the defaults first (R10.4).
*/
function ComboboxEmpty({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "combobox-empty",
		...properties,
		className: cn(COMBOBOX_EMPTY_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/dropzone.tsx
/**
* The classes the region carries (R10.5).
*
* A column centring its prompt, with the `input` role drawn as a dashed boundary at
* `rounded-hurdl-large` — the largest radius, matching a `Card`, because a drop region
* is an area rather than a field. `px-hurdl-6 py-hurdl-8` gives it the generous target a
* drop needs; `bg-card text-card-foreground` is the resting surface, and `gap-hurdl-2`
* spaces the prompt from the selected-file line. The focus ring is drawn when the hidden
* input it labels has focus — `has-[:focus-visible]`, so the region shows the control's
* focus even though the control itself is visually hidden. The rejected boundary and the
* disabled treatment follow `data-invalid` and `data-disabled` on the region, written as
* `data-[…]` variants the way `Switch`'s `data-[state=…]` fills are, so the region's own
* markup decides its appearance; the destructive border matches `Input`'s rejected one,
* and `opacity-50` is the one value that is not a token reference, there being no opacity
* Design_Token — `button.tsx` carries the reasoning. `w-full` because the region fills
* the measure its container gives it, as `Input` does.
*/
const DROPZONE_CLASS_NAMES = [
	"flex w-full flex-col items-center justify-center gap-hurdl-2",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-relaxed",
	"rounded-hurdl-large border border-dashed border-input px-hurdl-6 py-hurdl-8",
	"bg-card text-card-foreground",
	"cursor-pointer transition-colors duration-(--duration-hurdl-medium)",
	"has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
	"data-[invalid]:border-destructive",
	"data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
].join(" ");
/** The class the region adds while a drag is over it: the measured hover surface. */
const DROPZONE_DRAG_OVER_CLASS_NAMES = "bg-accent text-accent-foreground";
/** The classes the selected-file line carries: the dimmed foreground, as supporting text. */
const DROPZONE_FILES_CLASS_NAMES = "text-hurdl-caption leading-hurdl-tight text-muted-foreground";
/** The default prompt, shown when a caller supplies no children. */
const DEFAULT_PROMPT = "Drag files here, or activate to browse";
/** The files of a `DataTransfer` or an `input`, as a plain readonly array. */
function filesOf(list) {
	return list === null ? [] : Array.from(list);
}
/** The names of the selected files, joined for the line the region shows and announces. */
function describeSelection(files) {
	return files.map((file) => file.name).join(", ");
}
/**
* A drag-and-drop file input with a native picker fallback (R18.1).
*
* ```tsx
* <Dropzone
*   aria-label="Menu photograph"
*   accept="image/png,image/jpeg"
*   onFilesSelected={(files) => upload(files)}
* >
*   Drop a photograph here, or activate to browse
* </Dropzone>
* ```
*
* The region is a `label` naming the hidden file `input` inside it. A pointer click on
* the region opens the picker because clicking a label activates the control it names;
* the keyboard reaches the input in the tab order and opens the picker with `Enter` or
* `Space`, both the user agent's own behaviour. A drag over the region toggles the accent
* fill; a drop reads the files from the `DataTransfer` and reports them. The selected
* names are announced through an `aria-live` region without focus moving.
*
* The accessible name and every form property reach the input, as the control they
* belong on. `className` reaches the region, composed through `cn` with the defaults
* first (R10.4).
*/
function Dropzone({ onFilesSelected, invalid = false, disabled = false, className, children, id, ...inputProperties }) {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const [dragOver, setDragOver] = useState(false);
	const [selected, setSelected] = useState([]);
	const report = (files) => {
		setSelected(files);
		onFilesSelected?.(files);
	};
	const handleDragOver = (event) => {
		if (disabled) return;
		event.preventDefault();
		setDragOver(true);
	};
	const handleDragLeave = (event) => {
		event.preventDefault();
		setDragOver(false);
	};
	const handleDrop = (event) => {
		if (disabled) return;
		event.preventDefault();
		setDragOver(false);
		report(filesOf(event.dataTransfer.files));
	};
	return /* @__PURE__ */ jsxs("label", {
		"data-slot": "dropzone",
		htmlFor: inputId,
		"data-drag-over": dragOver ? "" : void 0,
		"data-invalid": invalid ? "" : void 0,
		"data-disabled": disabled ? "" : void 0,
		onDragOver: handleDragOver,
		onDragEnter: handleDragOver,
		onDragLeave: handleDragLeave,
		onDrop: handleDrop,
		className: cn(DROPZONE_CLASS_NAMES, dragOver && DROPZONE_DRAG_OVER_CLASS_NAMES, className),
		children: [
			/* @__PURE__ */ jsx("span", {
				"data-slot": "dropzone-prompt",
				children: children ?? DEFAULT_PROMPT
			}),
			/* @__PURE__ */ jsx("span", {
				"data-slot": "dropzone-files",
				"aria-live": "polite",
				className: DROPZONE_FILES_CLASS_NAMES,
				children: selected.length > 0 ? describeSelection(selected) : null
			}),
			/* @__PURE__ */ jsx("input", {
				"data-slot": "dropzone-input",
				id: inputId,
				type: "file",
				"aria-invalid": invalid ? true : void 0,
				disabled,
				...inputProperties,
				className: "sr-only",
				onChange: (event) => {
					report(filesOf(event.target.files));
				}
			})
		]
	});
}
//#endregion
//#region src/components/icon.tsx
/**
* The classes an icon carries, by size (R10.5).
*
* `shrink-0` so an icon in a flex row keeps its square rather than being squeezed, and
* a square sized from the spacing scale: `small` is `size-hurdl-4` (1rem, a caption's
* height), `medium` `size-hurdl-5` (1.25rem, beside body text), `large` `size-hurdl-6`
* (1.5rem, a section heading). The stroke is Lucide's own; the colour is
* `currentColor`, inherited, so no colour class is declared here.
*/
const iconClassNames = cva("shrink-0", {
	variants: { size: {
		small: "size-hurdl-4",
		medium: "size-hurdl-5",
		large: "size-hurdl-6"
	} },
	defaultVariants: { size: "medium" }
});
/**
* A Lucide glyph at a Design_Token size, in `currentColor` (R10.1).
*
* ```tsx
* <Icon icon={Search} />
* <Icon icon={Trash2} label="Delete" />
* <Icon icon={ArrowRight} size="small" animateOnHover />
* ```
*
* `className` composes through `cn` with the size first (R10.4). The icon takes the
* colour of the text around it; a caller changes it with a text colour on the icon or
* its container.
*/
function Icon({ icon: Glyph, size = "medium", label, animateOnHover = false, className }) {
	return /* @__PURE__ */ jsx(Glyph, {
		"data-slot": "icon",
		"aria-hidden": label === void 0 ? true : void 0,
		role: label === void 0 ? void 0 : "img",
		"aria-label": label,
		className: cn(iconClassNames({ size }), animateOnHover && "group-hover:hurdl-animate-rise", className)
	});
}
//#endregion
//#region src/components/sheet.tsx
/**
* The classes the scrim carries (R10.5).
*
* Identical to `DialogContent`'s scrim, because a sheet fades the page behind it the
* same way a dialog does: `fixed inset-0 z-50`, `bg-background opacity-50`, so the
* content behind fades halfway towards the theme's own base rather than towards a black
* the palette never declared. `dialog.tsx` records the whole of why the fill is the
* page surface rather than a literal, and `inset-0`/`z-50`/`opacity-50` are the
* allow-listed bare numbers that section names.
*/
const SHEET_OVERLAY_CLASS_NAMES = ["fixed inset-0 z-50", "bg-background opacity-50"].join(" ");
/**
* The classes a sheet surface carries, by the edge it enters from (R10.5).
*
* `class-variance-authority`, so the edge-independent base is declared once and each
* edge contributes only where it sits and which dimension it fills.
*
* The base is a floating panel: `fixed z-50`, the `bg-card text-card-foreground border
* border-border shadow-hurdl-high` a `Dialog` carries — the top elevation step, a
* surface over the whole page — and a flex column with `gap-hurdl-4` and `p-hurdl-6`
* so a header, a body and a footer stack with the padding a `Dialog` holds its content
* at. `overflow-y-auto` so a drawer taller than the viewport scrolls its own content.
*
* Each edge pins the surface to that edge and fills the cross axis, and it does so with
* `inset-0` and one `auto`: a `left` sheet is `inset-0 right-auto` — pinned to the top,
* bottom and left edges, its right edge free — and `h-full`, as tall as the viewport; a
* `right` sheet releases the left edge instead, a `top` sheet the bottom, a `bottom`
* sheet the top. `inset-0` is the allow-listed fill-the-viewport class (there is no zero
* step on the spacing scale, and the surface is portalled so it has no ancestor box),
* and `auto` is the keyword that frees the far edge so the panel sizes to its content
* along the entering axis rather than spanning the whole viewport. A single-side
* utility — `inset-y-0`, `left-0`, `border-r` — is outside the token vocabulary (the
* scan reads `left-0` as a bare scale step and `border-r` as naming `r`), which is the
* same finding `separator.tsx` and `table.tsx` record, so the inset-and-free-one-edge
* arrangement says the same thing in the vocabulary that is available.
*
* The border is a full `border border-border` on all four sides rather than the inward
* edge alone, for that reason: a single-side border is not expressible, and the far
* edge a full border adds is off the viewport anyway, so a drawer reads as bordered on
* the one edge that shows.
*
* The along-axis measure — how wide a side drawer is, how tall a top sheet is — is the
* content's: shadcn bounds a side sheet with `sm:max-w-sm`, an arbitrary width behind a
* breakpoint this package declares nowhere, and there is no width token large enough to
* express a drawer's measure, so the surface is as wide as what the caller puts inside
* it, which is the rule `Dialog` and `Card` keep. A caller wanting a fixed drawer width
* states it on the content it places inside.
*/
const sheetContentClassNames = cva([
	"fixed z-50 flex flex-col gap-hurdl-4 overflow-y-auto",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-relaxed",
	"border border-border p-hurdl-6",
	"bg-card text-card-foreground shadow-hurdl-high"
].join(" "), {
	variants: { side: {
		left: "inset-0 right-auto h-full hurdl-animate-slide-in-left",
		right: "inset-0 left-auto h-full hurdl-animate-slide-in-right",
		top: "inset-0 bottom-auto w-full hurdl-animate-slide-in-bottom",
		bottom: "inset-0 top-auto w-full hurdl-animate-slide-in-bottom"
	} },
	defaultVariants: { side: "left" }
});
/**
* The classes {@link SheetHeader} carries: the title and description stacked, as
* `DialogHeader`'s own.
*/
const SHEET_HEADER_CLASS_NAMES = "flex flex-col gap-hurdl-1";
/** The classes {@link SheetFooter} carries: the trailing-aligned row `DialogFooter` holds. */
const SHEET_FOOTER_CLASS_NAMES = "mt-auto flex items-center justify-end gap-hurdl-2";
/** The classes {@link SheetTitle} carries: `DialogTitle`'s weight and size. */
const SHEET_TITLE_CLASS_NAMES = "font-hurdl-semibold text-hurdl-subheading leading-hurdl-tight";
/** The classes {@link SheetDescription} carries: `DialogDescription`'s dimmed caption. */
const SHEET_DESCRIPTION_CLASS_NAMES = ["text-hurdl-caption leading-hurdl-relaxed", "text-muted-foreground"].join(" ");
/**
* A sheet: a modal surface entering from an edge of the viewport (R18.1).
*
* ```tsx
* <Sheet>
*   <SheetTrigger asChild>
*     <Button variant="secondary">Menu</Button>
*   </SheetTrigger>
*   <SheetContent side="left">
*     <SheetHeader>
*       <SheetTitle>Navigation</SheetTitle>
*     </SheetHeader>
*     …
*   </SheetContent>
* </Sheet>
* ```
*
* The composition is `Dialog`'s, part for part, because it is the same primitive: the
* root holds the state, the trigger opens it, the content is the surface, and the
* header, footer, title and description are the regions a `Dialog` has. A sheet is a
* dialog that enters from an edge, so a caller who knows one knows the other, which is
* what R18.2 asks of a second component built on the same foundation. The title is the
* surface's accessible name and so is not optional decoration, exactly as a dialog's
* is.
*
* No `data-slot` on this element: the primitive's root renders its children inside a
* provider and nothing of its own, as `dialog.tsx` records.
*/
function Sheet(properties) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, { ...properties });
}
/** The control that opens a {@link Sheet}: the primitive's trigger, `asChild` among its props. */
function SheetTrigger(properties) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Trigger, {
		"data-slot": "sheet-trigger",
		...properties
	});
}
/**
* The surface a {@link Sheet} opens: the scrim and the edge-anchored panel above it,
* portalled out of the page's own flow (R10.1).
*
* The portal, the scrim-before-surface order and the `ThemeScope` wrapper are
* `DialogContent`'s, and for the same reasons: the surface is positioned in the
* viewport's coordinates so it cannot be clipped by a container, the scrim is part of
* what a modal is rather than part of this component's public surface, and the theme
* has to be re-applied inside the portal or the panel resolves the light theme in both
* modes (`theme-scope.tsx` records the measured defect). `className` reaches the panel,
* composed through `cn` with the defaults first (R10.4).
*/
function SheetContent({ side = "left", className, children, ...properties }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Portal, { children: /* @__PURE__ */ jsxs(ThemeScope, { children: [/* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
		"data-slot": "sheet-overlay",
		className: SHEET_OVERLAY_CLASS_NAMES
	}), /* @__PURE__ */ jsx(DialogPrimitive.Content, {
		"data-slot": "sheet-content",
		...properties,
		className: cn(sheetContentClassNames({ side }), className),
		children
	})] }) });
}
/** The title and description of a {@link Sheet}, stacked (R10.1). */
function SheetHeader({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sheet-header",
		...properties,
		className: cn(SHEET_HEADER_CLASS_NAMES, className)
	});
}
/** The trailing row a {@link Sheet}'s decision is taken in (R10.1). */
function SheetFooter({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sheet-footer",
		...properties,
		className: cn(SHEET_FOOTER_CLASS_NAMES, className)
	});
}
/**
* What a {@link Sheet} is about: the primitive's `h2`, and the sheet's accessible name
* (R10.1).
*
* The primitive points the surface's `aria-labelledby` at this element, so a sheet
* names itself from its title exactly as a dialog does.
*/
function SheetTitle({ className, ...properties }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Title, {
		"data-slot": "sheet-title",
		...properties,
		className: cn(SHEET_TITLE_CLASS_NAMES, className)
	});
}
/** Supporting prose beneath a {@link SheetTitle}: the primitive's description (R10.1). */
function SheetDescription({ className, ...properties }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Description, {
		"data-slot": "sheet-description",
		...properties,
		className: cn(SHEET_DESCRIPTION_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/utilities/use-is-mobile.ts
/**
* The one media query this package reads, and why it is here rather than in a
* component source.
*
* `Sidebar` is a fixed column at and above the `md` breakpoint and a drawer below it,
* which is the one place a Primitive has to read the viewport's width. The breakpoint
* is Tailwind's own `md` (48rem) — a device fact rather than a value this theme
* declares, which is why no breakpoint Design_Token exists for it — and the only way to
* read it at runtime is a `matchMedia` query, whose argument is a length written out:
* `(max-width: 47.99rem)`, one hundredth of a rem below `md` so the query matches
* exactly the widths the `md:` utilities do not.
*
* That length is a literal, and `test/token-literals.property.test.ts` forbids a length
* literal in a component source — rightly, because a length in a component is a value a
* theme cannot reach. A breakpoint is the exception the rule does not want to admit:
* it is not a design value a theme would restyle but a fact about the device the layout
* responds to, and `Stack`'s `responsive` direction expresses the same breakpoint
* through the `md:` variant, which the scan admits because the base utility carries no
* scale. There is no `md:` variant for a `matchMedia` string, so the one place the
* breakpoint is written as a length lives here, in `src/utilities/` beside `cn.ts`,
* which the token scan does not cover for the same reason it does not cover a fixture:
* this is machinery, not a Primitive's appearance. A component importing it writes no
* length.
*/
/** Tailwind's `md` breakpoint, one hundredth of a rem below, so the query is "under md". */
const BELOW_MD_QUERY = "(max-width: 47.99rem)";
/**
* Whether the viewport is below the `md` breakpoint, updated as it changes.
*
* `false` on the server and in a jsdom render that never matches — the wide-screen
* state, which is what a fixture renders deterministically. In the browser it reflects
* the viewport and tracks a resize through the media query's own change event.
*/
function useIsMobile() {
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		if (typeof window === "undefined" || !("matchMedia" in window)) return;
		const query = window.matchMedia(BELOW_MD_QUERY);
		const update = () => {
			setIsMobile(query.matches);
		};
		update();
		query.addEventListener("change", update);
		return () => {
			query.removeEventListener("change", update);
		};
	}, []);
	return isMobile;
}
//#endregion
//#region src/components/sidebar.tsx
const SidebarContext = createContext(null);
/** The context, or a thrown error naming the part rendered outside a provider. */
function useSidebarContext(part) {
	const context = useContext(SidebarContext);
	if (context === null) throw new Error(`${part} must be rendered within a SidebarProvider.`);
	return context;
}
/**
* The sidebar's state, for a caller composing it (R18.1).
*
* The one piece of the sidebar an application legitimately reaches for: a navigation
* drawer that stays open after the Operator has chosen a destination is the usability
* fault every drawer has to answer, and closing it is the application's call, not this
* Primitive's — it is the application that owns the links and knows a navigation
* happened. So the provider's state is published through this hook rather than the
* component closing the drawer on a click it cannot attribute, exactly as shadcn's own
* `useSidebar` is the seam for the same job. It reads the context the parts read and
* renders nothing, so it is a hook and not a component: no fixture, no appearance to
* measure. Called outside a {@link SidebarProvider} it throws, naming itself, the way
* every part does.
*
* ```tsx
* const { isMobile, setOpenMobile } = useSidebar();
* // in a nav link's onClick: if (isMobile) setOpenMobile(false);
* ```
*/
function useSidebar() {
	const { state, isMobile, openMobile, setOpenMobile, toggle } = useSidebarContext("useSidebar");
	return {
		state,
		isMobile,
		openMobile,
		setOpenMobile,
		toggle
	};
}
/**
* The classes the fixed column carries (R10.5).
*
* A full-height column against the start edge: `h-full shrink-0`, a `border
* border-border` dividing it from the `main` beside it, and the card surface so it
* reads as a distinct region from the page. A full border rather than the inward
* `border-r` alone, for the reason `separator.tsx` records — a single-side border is
* outside the token vocabulary — and the outer edge it adds sits against the viewport's
* own, so the divider that shows is the inward one. The width is the one collapsible
* dimension — `w-hurdl-64` expanded, `w-hurdl-16` the icon rail — carried through a
* `data-[state=…]:` variant over the attribute the provider sets, with `transition-all
* duration-(--duration-hurdl-medium)` so the collapse is animated by a transition keyed
* to the duration token (and so zeroed under reduced motion), never a keyframe;
* `transition-all` rather than `transition-[width]` because an arbitrary property name
* in brackets is outside the vocabulary, and width is the only thing changing.
* `overflow-hidden` clips a label mid-collapse so it does not spill past the narrowing
* rail.
*/
const SIDEBAR_COLUMN_CLASS_NAMES = [
	"flex h-full shrink-0 flex-col overflow-hidden",
	"border border-border bg-card text-card-foreground",
	"transition-all duration-(--duration-hurdl-medium)",
	"data-[state=expanded]:w-hurdl-64 data-[state=collapsed]:w-hurdl-16"
].join(" ");
/** The classes the drawer's inner column carries: the regions stacked, full height. */
const SIDEBAR_DRAWER_CLASS_NAMES = "flex h-full flex-col gap-hurdl-4";
/** The classes {@link SidebarHeader} and {@link SidebarFooter} carry: a padded row. */
const SIDEBAR_HEADER_CLASS_NAMES = "flex shrink-0 items-center gap-hurdl-2 p-hurdl-4";
/** The classes {@link SidebarContent} carries: the scrolling middle region. */
const SIDEBAR_CONTENT_CLASS_NAMES = "flex grow flex-col gap-hurdl-2 overflow-y-auto p-hurdl-2";
/** The classes {@link SidebarMenu} carries: a gap-separated list with no bullets. */
const SIDEBAR_MENU_CLASS_NAMES = "flex w-full flex-col gap-hurdl-1";
/**
* The classes {@link SidebarMenuButton} carries (R10.5).
*
* A full-width row: the item's icon and label on one baseline (`flex items-center
* gap-hurdl-2`), `rounded-hurdl-medium px-hurdl-3 py-hurdl-2` the padding a control
* carries, and `text-hurdl-body` the body size. The resting state is the surface's own
* foreground on no fill; `hover:bg-accent hover:text-accent-foreground` is the accent
* hover surface an unfilled control takes, carried by `transition-colors
* duration-(--duration-hurdl-medium)`. The active page is marked by the accent fill
* held rather than hovered and the semibold weight, through the `data-[active=true]:`
* variant, so the current page reads as current by weight as well as fill (WCAG 1.4.1).
* `outline-none focus-visible:ring-2 focus-visible:ring-ring` is the platform focus
* ring. `whitespace-nowrap` keeps a label on one line, and `overflow-hidden` with it
* lets the rail clip the label when the column narrows.
*/
const SIDEBAR_MENU_BUTTON_CLASS_NAMES = [
	"flex w-full items-center gap-hurdl-2 overflow-hidden whitespace-nowrap",
	"rounded-hurdl-medium px-hurdl-3 py-hurdl-2 text-hurdl-body",
	"text-card-foreground",
	"transition-colors duration-(--duration-hurdl-medium)",
	"hover:bg-accent hover:text-accent-foreground",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring",
	"data-[active=true]:bg-accent data-[active=true]:text-accent-foreground data-[active=true]:font-hurdl-semibold"
].join(" ");
/**
* Holds the sidebar's state and the viewport fact, for the parts beneath it (R18.1).
*
* The open/collapsed state is controlled or uncontrolled in the shape `Select` and
* `Dialog` are: a supplied `state` wins and is reported through `onStateChange`, and an
* absent one is held here from `defaultState`. The mobile drawer's open state is held
* here throughout — it is transient, opened by the trigger and closed by the `Sheet`'s
* own dismissal — and `toggle` does the right thing for the viewport the provider is
* in: it opens or closes the drawer below `md` and expands or collapses the rail at and
* above it. The provider renders a `div` filling its box so the column and the `main`
* beside it share a flex row.
*/
function SidebarProvider({ state: controlledState, defaultState = "expanded", onStateChange, className, children, ...properties }) {
	const [internalState, setInternalState] = useState(defaultState);
	const [openMobile, setOpenMobile] = useState(false);
	const isMobile = useIsMobile();
	const state = controlledState ?? internalState;
	const setState = useCallback((next) => {
		if (controlledState === void 0) setInternalState(next);
		onStateChange?.(next);
	}, [controlledState, onStateChange]);
	const toggle = useCallback(() => {
		if (isMobile) {
			setOpenMobile((open) => !open);
			return;
		}
		setState(state === "expanded" ? "collapsed" : "expanded");
	}, [
		isMobile,
		state,
		setState
	]);
	const value = useMemo(() => ({
		state,
		openMobile,
		setOpenMobile,
		isMobile,
		toggle
	}), [
		state,
		openMobile,
		isMobile,
		toggle
	]);
	return /* @__PURE__ */ jsx(SidebarContext.Provider, {
		value,
		children: /* @__PURE__ */ jsx("div", {
			"data-slot": "sidebar-provider",
			...properties,
			className: cn("flex min-h-screen w-full", className),
			children
		})
	});
}
/**
* The navigation itself: a fixed column on a wide screen, a drawer on a narrow one
* (R18.1).
*
* Below the `md` breakpoint the content is rendered inside a {@link Sheet} entering from
* the left, opened by the {@link SidebarTrigger} and dismissed the way any sheet is; a
* `SheetTitle` and `SheetDescription` name the drawer for assistive technology, visually
* hidden with `sr-only` because the heading is the navigation's own. At and above `md`
* the same content is the fixed column, its width driven by the provider's state.
*
* The element is a `nav`, so the navigation is a landmark assistive technology can jump
* to; the accessible name is the caller's `aria-label`, as it is for every control that
* has no text of its own. `className` composes through `cn` with the defaults first
* (R10.4).
*/
function Sidebar({ className, children, ...properties }) {
	const { state, isMobile, openMobile, setOpenMobile } = useSidebarContext("Sidebar");
	if (isMobile) return /* @__PURE__ */ jsx(Sheet, {
		open: openMobile,
		onOpenChange: setOpenMobile,
		children: /* @__PURE__ */ jsxs(SheetContent, {
			side: "left",
			"data-slot": "sidebar-mobile",
			children: [/* @__PURE__ */ jsxs(SheetHeader, {
				className: "sr-only",
				children: [/* @__PURE__ */ jsx(SheetTitle, { children: "Navigation" }), /* @__PURE__ */ jsx(SheetDescription, { children: "The product and page navigation." })]
			}), /* @__PURE__ */ jsx("nav", {
				"aria-label": "Navigation",
				...properties,
				className: cn(SIDEBAR_DRAWER_CLASS_NAMES, className),
				children
			})]
		})
	});
	return /* @__PURE__ */ jsx("nav", {
		"data-slot": "sidebar",
		"data-state": state,
		"aria-label": "Navigation",
		...properties,
		className: cn(SIDEBAR_COLUMN_CLASS_NAMES, className),
		children
	});
}
/**
* The control that toggles the sidebar (R10.1).
*
* A native `button` carrying `aria-expanded`, so a screen-reader user is told whether
* the navigation is open; its press toggles the rail on a wide screen and the drawer on
* a narrow one, through the provider's `toggle`. The glyph is the caller's child, so the
* application supplies the mark it wants — this package ships no icon set. `className`
* composes through `cn` with the defaults first (R10.4).
*/
function SidebarTrigger({ className, children, onClick, ...properties }) {
	const { state, isMobile, openMobile, toggle } = useSidebarContext("SidebarTrigger");
	return /* @__PURE__ */ jsx("button", {
		type: "button",
		"data-slot": "sidebar-trigger",
		"aria-expanded": isMobile ? openMobile : state === "expanded",
		"aria-label": "Toggle navigation",
		...properties,
		onClick: (event) => {
			onClick?.(event);
			toggle();
		},
		className: cn([
			"inline-flex items-center justify-center shrink-0",
			"rounded-hurdl-medium p-hurdl-2 text-card-foreground",
			"transition-colors duration-(--duration-hurdl-medium)",
			"hover:bg-accent hover:text-accent-foreground",
			"outline-none focus-visible:ring-2 focus-visible:ring-ring"
		].join(" "), className),
		children
	});
}
/** The top region of a {@link Sidebar}: a logo and the trigger, padded (R10.1). */
function SidebarHeader({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sidebar-header",
		...properties,
		className: cn(SIDEBAR_HEADER_CLASS_NAMES, className)
	});
}
/** The scrolling middle region of a {@link Sidebar}, holding the menus (R10.1). */
function SidebarContent({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sidebar-content",
		...properties,
		className: cn(SIDEBAR_CONTENT_CLASS_NAMES, className)
	});
}
/** The bottom region of a {@link Sidebar}, padded (R10.1). */
function SidebarFooter({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sidebar-footer",
		...properties,
		className: cn(SIDEBAR_HEADER_CLASS_NAMES, className)
	});
}
/** A list of navigation items: a `ul` with no bullets (R10.1). */
function SidebarMenu({ className, ...properties }) {
	return /* @__PURE__ */ jsx("ul", {
		"data-slot": "sidebar-menu",
		...properties,
		className: cn(SIDEBAR_MENU_CLASS_NAMES, className)
	});
}
/** One navigation item: a `li` holding a {@link SidebarMenuButton} (R10.1). */
function SidebarMenuItem({ className, ...properties }) {
	return /* @__PURE__ */ jsx("li", {
		"data-slot": "sidebar-menu-item",
		...properties,
		className: cn("w-full", className)
	});
}
/**
* One navigation control (R10.1).
*
* A native `button` by default, or the caller's own child element under `asChild` — a
* router `Link` — carrying this control's classes and the active treatment, so the
* navigation's addresses stay the application's and no address literal reaches this
* Primitive. `active` sets `data-active` and `aria-current="page"`, so the current page
* is marked for assistive technology as well as by fill and weight. `className`
* composes through `cn` with the defaults first (R10.4).
*/
function SidebarMenuButton({ active = false, asChild = false, className, ...properties }) {
	return /* @__PURE__ */ jsx(asChild ? Slot : "button", {
		"data-slot": "sidebar-menu-button",
		"data-active": active,
		"aria-current": active ? "page" : void 0,
		...asChild ? {} : { type: "button" },
		...properties,
		className: cn(SIDEBAR_MENU_BUTTON_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/alert.tsx
/**
* The classes an `Alert` carries, by tone (R10.5).
*
* `class-variance-authority`, as the shadcn source does, so the default tone is
* declared beside the tones rather than restated in the component.
*
* The tone map is the one place in the rebuilt components that uses the platform's
* `hurdl-` colour utilities rather than shadcn's colour vocabulary, and the reason is
* that shadcn's vocabulary cannot express it: the alias block in `src/tokens.css`
* names one outcome role, `destructive`, and has no name for `informational`,
* `success` or `warning`. Writing one tone as `bg-destructive` and the other three as
* `bg-hurdl-success-background` would put two vocabularies in one record for no gain,
* so all four are stated in the platform's own terms. The aliases resolve to the same
* tokens either way.
*
* The pair is always the role's own. The palette declares exactly one foreground per
* background and measures the contrast of that pair alone (R9.9), so borrowing a
* colour from a neighbouring role would put an unmeasured pair on screen and quietly
* change what the tone means. The border repeats the foreground rather than
* introducing a third value, which is also what separates an `Alert` from the surface
* behind it when the two backgrounds are close in luminance.
*
* The base classes are a column with a gap rather than shadcn's icon grid — which is
* `grid grid-cols-[0_1fr] has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr]`, a
* reserved column for an icon this package does not ship — and the gap belongs to the
* `Alert` that decided upon it, so it disappears with the heading itself.
* `rounded-hurdl-medium` rather than shadcn's `rounded-lg`, and rather than the
* `Card`'s `hurdl-large`: an `Alert` is a message within a layout rather than the
* surface a layout is built on, and the smaller radius is the one `Button` and `Input`
* carry, which is the company an `Alert` keeps.
*/
const alertClassNames = cva([
	"flex w-full flex-col gap-hurdl-1",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-relaxed",
	"rounded-hurdl-medium border px-hurdl-4 py-hurdl-3"
].join(" "), {
	variants: { tone: {
		informational: ["border-hurdl-informational-foreground", "bg-hurdl-informational-background text-hurdl-informational-foreground"].join(" "),
		success: ["border-hurdl-success-foreground", "bg-hurdl-success-background text-hurdl-success-foreground"].join(" "),
		warning: ["border-hurdl-warning-foreground", "bg-hurdl-warning-background text-hurdl-warning-foreground"].join(" "),
		destructive: ["border-hurdl-error-foreground", "bg-hurdl-error-background text-hurdl-error-foreground"].join(" ")
	} },
	defaultVariants: { tone: "informational" }
});
/**
* The classes {@link AlertTitle} carries: the emphasis weight and the tighter line
* height, which is shadcn's `font-medium tracking-tight` in the platform's terms.
*
* No colour and no font size. The title takes the tone's foreground by inheritance,
* so a tone is declared in one place, and it is the same size as the content because
* weight is enough to separate a one-line heading from the prose beneath it — a larger
* step would make every `Alert` shout its first line. shadcn's `line-clamp-1` is
* dropped: silently truncating a message is the wrong failure for a component whose
* whole purpose is to be read.
*/
const ALERT_TITLE_CLASS_NAMES = "font-hurdl-semibold leading-hurdl-tight";
/**
* The classes {@link AlertDescription} carries.
*
* shadcn writes `text-muted-foreground text-sm`, and neither half survives contact
* with this palette: `muted-foreground` resolves to the ordinary foreground, and an
* `Alert`'s text must stay in its tone's foreground because that is the colour the
* contrast bound was measured for (R9.9). What remains is the line height prose reads
* at, inherited colour, and the grid of paragraph spacing shadcn gives a description
* holding more than one paragraph.
*/
const ALERT_DESCRIPTION_CLASS_NAMES = "grid justify-items-start gap-hurdl-1 leading-hurdl-relaxed";
/**
* A `div` element carrying a message in one of four semantic colour roles (R10.1).
*
* ```tsx
* <Alert>Your session will end in five minutes.</Alert>
* <Alert tone="destructive" title="Sign in failed">
*   The electronic mail address or password was not recognised.
* </Alert>
* <Alert tone="success">
*   <AlertTitle>Saved</AlertTitle>
*   <AlertDescription>Your changes are live.</AlertDescription>
* </Alert>
* ```
*
* `role="alert"` is written before the caller's properties are spread, so it is a
* default rather than a fixture: an `Alert` appearing in response to something the
* user did is announced when it arrives, which is the behaviour a rejected form
* submission needs, and a caller rendering a standing notice passes `role="status"` or
* `role="none"` and overrides it.
*
* The title is a `p` and not a heading, for the reason `CardTitle` is a `div`: a
* heading enters the document outline, and an `Alert` cannot know which level would be
* correct in a page it has never seen, so emitting one would risk breaking the heading
* order of every consumer that renders it. Emphasis by font weight makes the same
* visual distinction without making a structural claim.
*
* `className` is composed through `cn` with the defaults first (R10.4). `tone` is the
* supported way to change an `Alert`'s appearance.
*/
function Alert({ tone = "informational", title, className, children, ...properties }) {
	return /* @__PURE__ */ jsxs("div", {
		role: "alert",
		"data-slot": "alert",
		...properties,
		className: cn(alertClassNames({ tone }), className),
		children: [title !== void 0 && title.length > 0 ? /* @__PURE__ */ jsx(AlertTitle, { children: title }) : null, children]
	});
}
/** The heading of an {@link Alert}, as a `p` emphasised by weight (R10.1). */
function AlertTitle({ className, ...properties }) {
	return /* @__PURE__ */ jsx("p", {
		"data-slot": "alert-title",
		...properties,
		className: cn(ALERT_TITLE_CLASS_NAMES, className)
	});
}
/** The body of an {@link Alert}, which may hold several paragraphs (R10.1). */
function AlertDescription({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "alert-description",
		...properties,
		className: cn(ALERT_DESCRIPTION_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/table.tsx
/**
* The classes the scroll container carries.
*
* shadcn wraps the `table` in a `div` that scrolls sideways when the columns are
* wider than the space they have — `relative w-full overflow-x-auto` — rather than
* letting a wide table push the page's own layout out. That wrapper is kept: a
* compliance trail has many columns and is read in a panel narrower than it, so
* horizontal scroll is the behaviour a table wants and the one a bare `table` in a
* `Card` does not get.
*
* A full `border border-border` and `rounded-hurdl-medium` frame the whole table, as
* `Card`, `Select` and `Dialog` frame their surfaces, so the table reads as one
* region rather than a run of loose rows. `overflow-hidden` is what makes the radius
* clip the corners, and it sits beside `overflow-x-auto` on the same element without
* conflict — the horizontal axis scrolls and the rest is clipped.
*
* The border is a full box rather than a rule beneath each row: `tailwind-merge` and
* the token vocabulary this package bounds itself to (R10.5) express `border` with a
* colour token but not a single-side width, so the separation between rows is carried
* by the header's own emphasis and each row's hover fill rather than by a bottom rule.
* A ruled variant is a measured appearance change, which belongs with the rendered
* verification rather than here.
*/
const TABLE_CONTAINER_CLASS_NAMES = ["relative w-full overflow-x-auto overflow-hidden", "rounded-hurdl-medium border border-border"].join(" ");
/**
* The classes the `table` element carries (R10.5).
*
* shadcn writes `w-full caption-bottom text-sm`. `text-sm` becomes
* `text-hurdl-caption`, the platform's smallest step, because a dense table reads a
* step below body text and that is the token that expresses it; `caption-bottom`
* keeps the `TableCaption` beneath the rows it describes, which is where a caption
* belongs and where a screen reader announces it from. The typography is stated
* here rather than inherited, for the reason `Card` states its own: a table holds
* text belonging to no other component, and text inheriting the user agent's serif
* default inside an otherwise tokenised surface is the failure this prevents.
*
*/
const TABLE_CLASS_NAMES = ["w-full caption-bottom", "font-hurdl-sans font-hurdl-regular text-hurdl-caption leading-hurdl-relaxed"].join(" ");
/**
* The classes {@link TableRow} carries (R10.5).
*
* A hover fill, and nothing more. `hover:bg-accent` is the hover surface, `accent`
* being the palette's role for the hover state of an otherwise unfilled surface — the
* same role `SelectItem` uses for its highlight. shadcn writes `hover:bg-muted/50`,
* an opacity over the muted fill; this package has no opacity token and expresses the
* hover as a role instead, so the colour stays measured (R9.9).
* `data-[state=selected]:bg-accent` keeps a selected row filled by the same role, the
* attribute being the one a consumer's own selection sets rather than anything this
* component reads.
*
* `transition-colors` with the medium duration token, so a hover fades rather than
* snapping and honours the reduced-motion preference through the token.
*
* No row rule: a single-side border is outside the token vocabulary R10.5 bounds this
* file to, so the rows are separated by the header band above them
* ({@link TABLE_HEAD_CLASS_NAMES}) and the hover fill rather than by a line between
* each pair.
*/
const TABLE_ROW_CLASS_NAMES = ["hover:bg-accent data-[state=selected]:bg-accent", "transition-colors duration-(--duration-hurdl-medium)"].join(" ");
/**
* The classes {@link TableHead} carries (R10.5).
*
* A column heading: a subdued band, the emphasis weight, the dimmed foreground and
* the padding the cells share. shadcn writes `h-10 px-2 text-left align-middle
* font-medium text-foreground`; the height follows from the padding here rather than
* being fixed, `px-2` becomes `px-hurdl-3` and a matching `py-hurdl-2`, and the
* `bg-muted text-muted-foreground` pair fills the header band and dims its text —
* `muted` being the palette's subdued surface and its paired foreground clearing
* 4.5:1 on it (R9.9). The band is what separates the header from the body in the
* absence of a rule beneath it, and it is the role a heading wants: secondary to the
* values below. `text-left` is the default alignment a caller overrides per column;
* `align-middle` centres the text against a cell taller than one line.
* `whitespace-nowrap` keeps a heading on one line, the scroll container being what a
* wide row of them reaches.
*/
const TABLE_HEAD_CLASS_NAMES = ["px-hurdl-3 py-hurdl-2 text-left align-middle whitespace-nowrap", "font-hurdl-semibold bg-muted text-muted-foreground"].join(" ");
/**
* The classes {@link TableCell} carries (R10.5).
*
* The same padding as the heading, so a cell and the heading above it line up, and
* `align-middle` for the same reason. shadcn's `p-2` becomes the `px-hurdl-3
* py-hurdl-2` pair the heading carries; no colour, so a cell takes the surface's own
* foreground.
*/
const TABLE_CELL_CLASS_NAMES = "px-hurdl-3 py-hurdl-2 align-middle";
/**
* The classes {@link TableCaption} carries: shadcn's `text-muted-foreground mt-4
* text-sm` in the platform's terms.
*
* The dimmed foreground, the smallest step, and a top margin separating the caption
* from the rows above it — `mt-4` becomes `mt-hurdl-4`. `caption-bottom` on the
* table is what places it beneath them.
*/
const TABLE_CAPTION_CLASS_NAMES = ["mt-hurdl-4 text-hurdl-caption text-muted-foreground"].join(" ");
/**
* A native `table`, in a container that scrolls sideways when its columns overflow
* (R10.1, R20.4).
*
* ```tsx
* <Table>
*   <TableCaption>Recent audit entries.</TableCaption>
*   <TableHeader>
*     <TableRow>
*       <TableHead>When</TableHead>
*       <TableHead>Action</TableHead>
*       <TableHead>Outcome</TableHead>
*     </TableRow>
*   </TableHeader>
*   <TableBody>
*     <TableRow>
*       <TableCell>2024-03-01T10:00:00Z</TableCell>
*       <TableCell>account:password:changed</TableCell>
*       <TableCell>succeeded</TableCell>
*     </TableRow>
*   </TableBody>
* </Table>
* ```
*
* Seven names, and the composition is native HTML's rather than a component
* configured by properties, for the reason `select.tsx` records of its own: a caller
* cannot express a table with fewer of them, and the arrangement is what lets a call
* site put its own content in a cell without this package growing a property for
* every arrangement. It is `<table>`, `<thead>`, `<tbody>`, `<tr>`, `<th>`, `<td>`
* and `<caption>` — the real table elements — so the column associations a screen
* reader reads come from `<th scope>` markup rather than from ARIA over `div`s, which
* is what R20.4 asks for and what a `role="table"` over a grid of `div`s would only
* approximate.
*
* shadcn's `TableFooter` is deliberately absent, on the reasoning `select.tsx`
* applies to the five names it omits: no screen this feature adds renders a table
* footer, and R18.9 asks that an addition be driven by a caller rather than by
* anticipation. Adding it later is additive.
*
* The `table` is wrapped in a scroll container, so `className` composes onto the
* `table` itself while the wrapper's classes are the component's own — a caller
* styling the table styles the table rather than the box around it. `data-slot` sits
* on both, so a consumer's stylesheet and this package's tests can reach either.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function Table({ className, ...properties }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "table-container",
		className: TABLE_CONTAINER_CLASS_NAMES,
		children: /* @__PURE__ */ jsx("table", {
			"data-slot": "table",
			...properties,
			className: cn(TABLE_CLASS_NAMES, className)
		})
	});
}
/** The heading rows of a {@link Table}, as a `thead` (R10.1). */
function TableHeader({ className, ...properties }) {
	return /* @__PURE__ */ jsx("thead", {
		"data-slot": "table-header",
		...properties,
		className
	});
}
/** The body rows of a {@link Table}, as a `tbody` (R10.1). */
function TableBody({ className, ...properties }) {
	return /* @__PURE__ */ jsx("tbody", {
		"data-slot": "table-body",
		...properties,
		className
	});
}
/**
* One row of a {@link Table}, as a `tr` filled on hover (R10.1).
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function TableRow({ className, ...properties }) {
	return /* @__PURE__ */ jsx("tr", {
		"data-slot": "table-row",
		...properties,
		className: cn(TABLE_ROW_CLASS_NAMES, className)
	});
}
/**
* A column heading of a {@link Table}, as a `th` scoping its column (R10.1, R20.4).
*
* `scope` defaults to `col`, written before the caller's properties are spread so it
* is a default rather than a fixture: a `th` in a header row heads its column, and
* emitting `scope="col"` is what associates every cell beneath it with it for a
* screen reader. A caller heading a row instead passes `scope="row"` through the
* native properties and overrides it.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function TableHead({ className, ...properties }) {
	return /* @__PURE__ */ jsx("th", {
		scope: "col",
		"data-slot": "table-head",
		...properties,
		className: cn(TABLE_HEAD_CLASS_NAMES, className)
	});
}
/**
* One cell of a {@link Table}, as a `td` (R10.1).
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function TableCell({ className, ...properties }) {
	return /* @__PURE__ */ jsx("td", {
		"data-slot": "table-cell",
		...properties,
		className: cn(TABLE_CELL_CLASS_NAMES, className)
	});
}
/**
* The caption of a {@link Table}, as a `caption` beneath its rows (R10.1).
*
* A `caption` rather than a paragraph beside the table, because it is the element a
* screen reader announces as the table's name and the one `caption-bottom` places
* beneath the rows. `className` is composed through `cn` with the defaults first
* (R10.4).
*/
function TableCaption({ className, ...properties }) {
	return /* @__PURE__ */ jsx("caption", {
		"data-slot": "table-caption",
		...properties,
		className: cn(TABLE_CAPTION_CLASS_NAMES, className)
	});
}
//#endregion
//#region src/components/form-field.tsx
/**
* The classes the wrapper carries: a vertical stack, spaced by a Design_Token.
*
* Every value is a Design_Token reference (R10.5). The stack is written here rather
* than composed from `Stack` because a `FormField` is one fixed arrangement — label,
* control, description, error — and is not a layout a caller configures; taking
* `direction`, `gap` and `align` from a layout primitive would offer three
* properties that must not vary.
*/
const BASE_CLASS_NAMES = "flex flex-col gap-hurdl-2";
/**
* The classes the description carries: the muted foreground at the caption size, on
* whatever surface the field sits on.
*
* `text-muted-foreground` is shadcn's name for it, and in this palette it resolves to
* a colour of its own: ADR 0004's `muted-foreground` is dimmer than every surface's
* paired foreground and clears 4.5:1 on all four of them, as `src/tokens.css` records,
* so the dimming and the caption size distinguish a description from the content
* around it together.
*
* The relaxed line height rather than the tight one the `Label` uses: a description
* is prose that may wrap onto several lines, where a label is a name that rarely
* does.
*/
const DESCRIPTION_CLASS_NAMES = ["font-hurdl-sans font-hurdl-regular text-hurdl-caption leading-hurdl-relaxed", "text-muted-foreground"].join(" ");
/**
* The classes the error message carries: the destructive role's foreground, the
* same colour the `Label`'s required indicator and an invalid `Input`'s border are
* drawn in, so that the three read as one convention.
*
* Semibold rather than regular, which is the one thing distinguishing it from the
* description at a glance. Colour alone would carry the distinction for most users
* and for none who cannot separate those hues, and the weight costs nothing.
*/
const ERROR_CLASS_NAMES = ["font-hurdl-sans font-hurdl-semibold text-hurdl-caption leading-hurdl-relaxed", "text-destructive-foreground"].join(" ");
/** The suffix distinguishing the description element's identifier from the field's. */
const DESCRIPTION_ID_SUFFIX = "-description";
/** The suffix distinguishing the error element's identifier from the field's. */
const ERROR_ID_SUFFIX = "-error";
/**
* Whether a supplied message is one the field should render.
*
* An absent value and an empty string are treated alike, which is what keeps the
* attributes honest: `aria-describedby` may only name elements that exist (R10.11),
* so a description of no characters must render no element and contribute no
* identifier. `error` is read the same way because R10.7 speaks of an error message
* of one or more characters.
*/
function hasText(value) {
	return value !== void 0 && value.length > 0;
}
/**
* A labelled form field that owns the identifiers and the ARIA wiring between a
* control, its label, its description and its error message (R10.2).
*
* ```tsx
* <FormField
*   label="Electronic mail address"
*   description="We will only use this to contact you."
*   error={errors.address}
*   required
* >
*   {(control) => (
*     <Input
*       {...control}
*       type="email"
*       required
*       value={address}
*       onChange={onAddressChange}
*     />
*   )}
* </FormField>
* ```
*
* Identifiers derive from React's `useId`, which yields a value unique to the
* instance within the document, so R10.13 holds for any number of fields without a
* counter to keep or a prop to thread through. The field's own identifier is the
* control's `id`, and the two elements that describe it suffix it, so all three are
* one `useId` call.
*
* `aria-describedby` names exactly what was rendered, in the order description then
* error, and `aria-invalid` is present exactly when a non-empty error was supplied:
*
* | description | error   | `aria-describedby`            | `aria-invalid` |
* | ----------- | ------- | ----------------------------- | -------------- |
* | absent      | absent  | omitted                       | omitted        |
* | present     | absent  | `<id>-description`            | omitted        |
* | absent      | present | `<id>-error`                  | `true`         |
* | present     | present | `<id>-description <id>-error` | `true`         |
*
* The first row is R10.8, the second R10.12, the third R10.7 and the fourth R10.11.
* All four fall out of one rule rather than four branches: the identifiers are
* collected from the same two conditions that decide whether the elements are
* rendered, so the attribute cannot come to name an element that is not there.
*
* The error message is a plain paragraph rather than a live region. The association
* `aria-describedby` makes is what announces it when the control takes focus, and a
* `role="alert"` would announce the same text a second time, out of turn, every time
* a validation pass rewrote it.
*
* `className` is composed through `cn` with the defaults first (R10.4) and applies to
* the wrapper; the control's classes are the caller's, passed to whatever it renders.
*
* There is no React Hook Form here, and that is deliberate rather than pending.
* shadcn's `form.tsx` is coupled to React Hook Form's `FormProvider`, `Controller`
* and `useFormContext`, and adopting it would make every consumer of a single field
* adopt a form library. `DESIGN_SYSTEM.md` does name React Hook Form as the platform's
* form management library, so this is a deferral and not a rejection: `FormField`
* stays presentational, a form-aware component can be added beside it, and ADR 0003
* records the deferral.
*/
function FormField({ label, description, error, required = false, className, children }) {
	const fieldId = useId();
	const descriptionId = `${fieldId}${DESCRIPTION_ID_SUFFIX}`;
	const errorId = `${fieldId}${ERROR_ID_SUFFIX}`;
	const showDescription = hasText(description);
	const showError = hasText(error);
	const describedByIds = [...showDescription ? [descriptionId] : [], ...showError ? [errorId] : []];
	const control = {
		id: fieldId,
		...describedByIds.length > 0 ? { "aria-describedby": describedByIds.join(" ") } : {},
		...showError ? { "aria-invalid": true } : {}
	};
	return /* @__PURE__ */ jsxs("div", {
		"data-slot": "form-field",
		className: cn(BASE_CLASS_NAMES, className),
		children: [
			/* @__PURE__ */ jsx(Label, {
				htmlFor: fieldId,
				required,
				children: label
			}),
			children(control),
			showDescription ? /* @__PURE__ */ jsx("p", {
				id: descriptionId,
				className: DESCRIPTION_CLASS_NAMES,
				children: description
			}) : null,
			showError ? /* @__PURE__ */ jsx("p", {
				id: errorId,
				className: ERROR_CLASS_NAMES,
				children: error
			}) : null
		]
	});
}
//#endregion
//#region src/components/tooltip.tsx
/**
* The gap between the trigger and the surface, as shadcn ships it.
*
* A number the primitive applies as an inline offset rather than a class, so it is not
* a length R10.5 governs — the criterion speaks of a value written into a `class`
* attribute, and this is a property the primitive measures from the trigger. It is
* named here rather than written inline so that the one place a tooltip's standoff is
* decided is visible, and a caller overrides it through {@link TooltipContentProps}.
*/
const SIDE_OFFSET = 4;
/**
* The classes the surface carries (R10.5).
*
* A tooltip is a small floating surface, so it takes the popover colour pair rather
* than a card's: `bg-popover text-popover-foreground`, which the alias block in
* `src/tokens.css` resolves onto the `card` role, with the elevation telling a floating
* surface from a resting one as it does for `SelectContent`. `shadow-hurdl-medium` is
* one step below the surface a `Select` or a `Dialog` opens — a tooltip hovers close to
* the thing it labels rather than over the whole page — and `border border-border` with
* `rounded-hurdl-small` matches the smallest radius a floating chip is read as, a step
* below the surface a list opens in.
*
* `px-hurdl-2 py-hurdl-1` is shadcn's padding on the platform's spacing scale, and
* `text-hurdl-caption` the size a label beside a control wants: a tooltip is secondary
* to the thing it describes, so it is set smaller than body text and in the surface's
* own foreground.
*
* `z-50` is the stacking order the portalled surfaces of `Select` and `Dialog` already
* needed: the surface is portalled to the end of the document, so it paints above the
* page already, but a tooltip opened from a control inside another portalled surface
* has to say so. `test/token-literals.property.test.ts` records it in its allow-list
* with that reason, a z-index being neither a colour, a length nor a duration.
*
* `w-fit` so the surface is only as wide as its label, and no maximum width, for the
* reason `DialogContent` records: a measure belongs to the content, in the one place
* that knows what the text is.
*
* No entrance keyframe. shadcn writes `data-[state=…]:animate-…` and
* `data-[side=…]:slide-in-from-…`, which are `tailwindcss-animate` — a dependency this
* package does not apply — and a fade here would carry the reduced-motion preference as
* a duration on a transition rather than on a keyframe, as `SelectContent` and
* `DialogContent` record. A tooltip appearing at once is both correct and the simplest
* of the three, so there is no transition at all.
*/
const TOOLTIP_CONTENT_CLASS_NAMES = [
	"z-50 w-fit",
	"font-hurdl-sans font-hurdl-regular text-hurdl-caption leading-hurdl-tight",
	"rounded-hurdl-small border border-border px-hurdl-2 py-hurdl-1",
	"bg-popover text-popover-foreground shadow-hurdl-medium",
	"hurdl-animate-fade-in"
].join(" ");
/**
* The classes the arrow carries.
*
* The primitive renders the arrow as an `svg`, so its colour is a `fill` rather than a
* `background`: `fill-popover` matches the surface's own fill, which is what makes the
* arrow read as part of the surface rather than as a mark beside it. `fill-` is one of
* the colour prefixes `test/token-literals.property.test.ts` governs and `popover` is a
* declared alias, so the class is a token reference at one remove the way
* `bg-popover` is.
*
* No border on the arrow. shadcn draws one by rotating a bordered square, which this
* package has no need of: the surface's own border frames the label, and the arrow is a
* solid wedge in the surface's fill pointing at the trigger.
*/
const TOOLTIP_ARROW_CLASS_NAMES = "fill-popover";
/**
* The context a {@link Tooltip} is rendered under: the shared timing a group of them
* agree on (R18.1).
*
* ```tsx
* <TooltipProvider>
*   <Tooltip>
*     <TooltipTrigger asChild>
*       <Button variant="ghost">Why?</Button>
*     </TooltipTrigger>
*     <TooltipContent>Ends every other session but this one.</TooltipContent>
*   </Tooltip>
* </TooltipProvider>
* ```
*
* Rendered once around the tooltips of a region rather than around each, which is the
* primitive's own arrangement: the provider holds the delay a tooltip opens after and
* the window in which a second tooltip opens without the delay again, so a row of
* controls each with a tooltip reads as one surface moving between them rather than as
* each one waiting afresh. A single `Tooltip` still needs a provider above it, which is
* why all four names are exported together.
*
* No `data-slot` and no class, because there is no element: the provider renders its
* children inside a context and nothing of its own, which is what `select.tsx` and
* `dialog.tsx` record of their roots.
*/
function TooltipProvider(properties) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Provider, { ...properties });
}
/**
* A tooltip: a small floating label that appears beside a control while it is focused
* or hovered (R18.1).
*
* `Tooltip` provides the state, {@link TooltipTrigger} is the control it describes, and
* {@link TooltipContent} is the surface. The composition is shadcn's, as `Dialog`'s and
* `Select`'s are.
*
* Everything the tooltip *does* is the primitive's and the user agent's: the open on
* hover and on keyboard focus, the dismissal on blur and on the escape key, the delay
* the provider governs, and the `aria-describedby` wiring the trigger to the surface so
* assistive technology announces the label as the control's description (R18.5). None
* of that is reimplemented here, which is invariant 8 of `AGENTS.md` and the reason ADR
* 0003 chose this foundation.
*
* No `data-slot` on this element, because there is no element: the primitive's root
* renders its children inside a provider and nothing of its own, which is what
* `select.tsx` records of its own root.
*/
function Tooltip(properties) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Root, { ...properties });
}
/**
* The control a {@link Tooltip} describes: the element the surface points at and is
* wired to by `aria-describedby` (R10.1, R10.3).
*
* No class of its own, as in shadcn's source, and that is what makes the trigger a
* behaviour rather than an appearance: a call site wraps the control it already wanted
* — usually a `Button` — with `asChild`, and the trigger contributes the hover and
* focus handling and the ARIA wiring to it. `asChild` is the primitive's own property
* rather than one added here, as `DialogTrigger`'s is.
*/
function TooltipTrigger(properties) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Trigger, {
		"data-slot": "tooltip-trigger",
		...properties
	});
}
/**
* The surface a {@link Tooltip} shows: the label, portalled out of the page's own flow
* (R10.1).
*
* The portal is shadcn's and it is not optional, for the reason `SelectContent` and
* `DialogContent` record: a tooltip rendered inside a `Card` or anything carrying
* `overflow: hidden` would otherwise be clipped by its container, and the surface is
* positioned in the viewport's coordinates.
*
* `ThemeScope` wraps the surface, and it is what makes the theme survive the portal.
* Appending the surface near the end of the document takes it out from under the
* `ThemeSurface` whose class the theme's custom properties are declared under, so
* without it a tooltip opened over a dark page drew the *light* popover colour — the
* exact defect invariant 10 of `AGENTS.md` records of `Select` and `Dialog`. It
* re-applies the theme and the mode the surface published and generates no box of its
* own, so the surface stays positioned in the viewport's coordinates;
* `src/theming/theme-scope.tsx` records the whole of it.
*
* The arrow is rendered inside the surface, so it takes the scope's theme with it, and
* it carries no `className`: it is part of what the surface *is* rather than part of
* this component's public surface, and a caller who could restyle it could pull the
* wedge away from the fill it is meant to match.
*
* `className` reaches the surface itself rather than the portal, the scope or the arrow
* (R10.4), composed through `cn` with the defaults first, so a supplied utility
* overrides the default it conflicts with and every default it does not conflict with
* survives. `sideOffset` defaults to {@link SIDE_OFFSET} and reaches the primitive,
* which applies it as the gap between the trigger and the surface.
*/
function TooltipContent({ className, children, sideOffset = SIDE_OFFSET, ...properties }) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx(ThemeScope, { children: /* @__PURE__ */ jsxs(TooltipPrimitive.Content, {
		"data-slot": "tooltip-content",
		sideOffset,
		...properties,
		className: cn(TOOLTIP_CONTENT_CLASS_NAMES, className),
		children: [children, /* @__PURE__ */ jsx(TooltipPrimitive.Arrow, {
			"data-slot": "tooltip-arrow",
			className: TOOLTIP_ARROW_CLASS_NAMES
		})]
	}) }) });
}
//#endregion
//#region src/components/toast.tsx
/**
* The classes the viewport carries (R10.5).
*
* `fixed` with an inset of one spacing step from the bottom-right corner — `bottom-hurdl-4
* right-hurdl-4` — which is where a report of an outcome belongs: out of the way of the
* work, and read last. It is a `position: fixed` region rather than a portalled surface,
* so it needs `z-50` to sit above the page the way the portalled surfaces do; the
* stacking order is in `test/token-literals.property.test.ts`'s allow-list with that
* reason.
*
* `flex flex-col gap-hurdl-2` stacks several toasts with one spacing step between them,
* and `max-h-screen` keeps the column from growing past the viewport — a fact about the
* container rather than a length from a scale, the same keyword `ThemeSurface`'s
* `min-h-screen` is. `w-full` and no maximum width: shadcn bounds the column with
* `sm:max-w-[420px]`, which is an arbitrary length behind a breakpoint this package
* declares nowhere, and there is no width token large enough to express a toast's measure
* — the spacing scale tops at 4rem — so the column spans the gutter it is inset within
* and the content inside each toast decides its own measure, which is the rule `Card` and
* `Dialog` keep. `p-hurdl-4` is shadcn's `p-4` on the platform's scale, so the first and
* last toast are inset from the viewport's own edge.
*/
const TOAST_VIEWPORT_CLASS_NAMES = [
	"fixed inset-0 top-auto z-50",
	"md:inset-auto md:right-hurdl-4 md:bottom-hurdl-4",
	"flex max-h-screen flex-col gap-hurdl-2 p-hurdl-4"
].join(" ");
/**
* The classes a toast carries, by tone (R10.5).
*
* `class-variance-authority`, as `alert.tsx` and `badge.tsx` do, so the default tone is
* declared beside the tones rather than restated in the component.
*
* A card surface with a tone-coloured leading edge, rather than a fully tinted surface.
* The base is a floating card — `bg-card text-card-foreground border border-border` at
* `shadow-hurdl-high`, the top elevation step a surface that floats over the whole page
* carries, the same a `Dialog` has — so a toast reads as the neutral surface a report
* sits on in both themes. The tone is shown as the colour of the surface's leading
* border alone: `border-l-hurdl-success-foreground` and its fellows recolour the left
* edge in the status role's measured foreground while the other three edges stay
* `border-border`. The pair the tint would otherwise put on screen is never drawn, so no
* unmeasured colour pair reaches the surface — the card pair is the measured one (R9.9)
* and the tone is a mark on the frame rather than a fill behind the text.
*
* The base is a row: the title and description stack in a column the caller composes, and
* the close control sits at the trailing edge, so `flex items-start justify-between
* gap-hurdl-4` places the two with a spacing step between them and the control aligned to
* the top of a multi-line report. `rounded-hurdl-medium px-hurdl-4 py-hurdl-3` is the
* padding and radius an `Alert` carries, which is the company a report keeps.
*
* No entrance keyframe. shadcn's `data-[state=…]:animate-…` and `data-[swipe=…]:…`
* classes are `tailwindcss-animate`, which this package does not apply, and a transition
* here would carry the reduced-motion preference as a duration on a transition rather
* than on a keyframe, as `SelectContent` records.
*/
const toastClassNames = cva([
	"flex items-start gap-hurdl-3 w-full",
	"font-hurdl-sans font-hurdl-regular text-hurdl-body leading-hurdl-relaxed",
	"rounded-hurdl-medium border px-hurdl-4 py-hurdl-3",
	"shadow-hurdl-high",
	"hurdl-animate-rise"
].join(" "), {
	variants: { tone: {
		informational: ["border-hurdl-informational-foreground", "bg-hurdl-informational-background text-hurdl-informational-foreground"].join(" "),
		success: ["border-hurdl-success-foreground", "bg-hurdl-success-background text-hurdl-success-foreground"].join(" "),
		warning: ["border-hurdl-warning-foreground", "bg-hurdl-warning-background text-hurdl-warning-foreground"].join(" "),
		destructive: ["border-hurdl-error-foreground", "bg-hurdl-error-background text-hurdl-error-foreground"].join(" ")
	} },
	defaultVariants: { tone: "informational" }
});
/**
* The Lucide glyph each tone shows at its leading edge, so the outcome is read by a
* mark as well as by colour (WCAG 1.4.1 — colour is never the only carrier). Each is a
* conventional status glyph: a tick for success, a cross-in-a-circle for a failure, a
* triangle for a warning, an `i` for a plain report. The icon inherits the tone's
* foreground through `currentColor`, so it is the measured pair the surface already
* carries rather than a colour of its own.
*/
const TONE_ICON = {
	informational: Info,
	success: CheckCircle2,
	warning: TriangleAlert,
	destructive: CircleX
};
/**
* The classes {@link ToastTitle} carries: the emphasis weight and the tighter line
* height, which is `AlertTitle`'s own.
*
* No colour and no font size. The title takes the surface's foreground, so a toast's
* colours are declared in one place, and it is the same size as the description because
* weight is enough to separate a one-line heading from the prose beneath it, which is the
* distinction `AlertTitle` draws.
*/
const TOAST_TITLE_CLASS_NAMES = "font-hurdl-semibold leading-hurdl-tight";
/**
* The classes {@link ToastDescription} carries: the caption size and the dimmed
* foreground, which is `CardDescription`'s own and what distinguishes supporting prose
* from the title above it. `muted-foreground` is a measured colour clearing 4.5:1 on
* every surface (R9.9), so the dimming is real and the size is not carrying it alone.
*/
const TOAST_DESCRIPTION_CLASS_NAMES = ["text-hurdl-caption leading-hurdl-relaxed"].join(" ");
/**
* The classes {@link ToastClose} carries.
*
* `shrink-0` so the control is not squeezed by a long report, and the focus ring the
* platform draws on every control: `focus-visible:ring-2 focus-visible:ring-ring`, with
* `outline-none` suppressing the user agent's own so the two do not stack.
* `rounded-hurdl-small` rounds the hit area, and `text-muted-foreground` dims the glyph
* so it reads as secondary to the report — the dismissal is available rather than the
* point. `transition-colors duration-(--duration-hurdl-medium)` carries the glyph to its
* hover colour through the duration token, which is how the reduced-motion preference
* reaches it with no JavaScript, as `switch.tsx` records; `hover:text-card-foreground`
* brings it to the surface's full foreground on hover.
*/
const TOAST_CLOSE_CLASS_NAMES = [
	"shrink-0 rounded-hurdl-small opacity-50",
	"transition-opacity duration-(--duration-hurdl-medium)",
	"hover:opacity-100",
	"outline-none focus-visible:ring-2 focus-visible:ring-ring"
].join(" ");
/**
* The context a {@link Toast} is rendered under, and the region it is rendered into
* (R18.1).
*
* ```tsx
* <ToastProvider>
*   <Toast tone="success" open={reviewed} onOpenChange={setReviewed}>
*     <div>
*       <ToastTitle>Reviewed Big Mac</ToastTitle>
*       <ToastDescription>Your rating is saved.</ToastDescription>
*     </div>
*     <ToastClose />
*   </Toast>
*   <ToastViewport />
* </ToastProvider>
* ```
*
* Six names, and the composition is shadcn's. `ToastProvider` holds the state a group of
* toasts share, {@link Toast} is one report, {@link ToastTitle} and
* {@link ToastDescription} name and describe it, {@link ToastClose} dismisses it, and
* {@link ToastViewport} is the fixed region they stack in. The provider wraps both the
* toasts and the viewport, which is the primitive's own arrangement.
*
* `ToastAction` is deliberately absent — shadcn's seventh name — because R18.9 asks that
* an addition be driven by a caller and the reports this feature adds are feedback rather
* than a prompt to act: a toast confirming "Saved" has nothing to undo from itself, and a
* caller that later needs an action in a toast adds it then, with a fixture of its own.
*
* No `data-slot` and no class, because there is no element: the provider renders its
* children inside a context and nothing of its own, which is what `select.tsx` and
* `dialog.tsx` record of their roots.
*/
function ToastProvider(properties) {
	return /* @__PURE__ */ jsx(ToastPrimitive.Provider, { ...properties });
}
/**
* The fixed region a {@link ToastProvider}'s toasts stack in: an ordered list anchored
* to the bottom-right of the viewport (R10.1).
*
* It renders in place rather than through a portal — the primitive gives it
* `position: fixed`, so it is lifted out of the page's flow without leaving the document
* tree — which is why no `ThemeScope` is needed and the note at the head of this file
* records why. A `Toaster` rendered inside a `ThemeSurface` therefore resolves that
* surface's theme by inheritance.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function ToastViewport({ className, ...properties }) {
	return /* @__PURE__ */ jsx(ToastPrimitive.Viewport, {
		"data-slot": "toast-viewport",
		...properties,
		className: cn(TOAST_VIEWPORT_CLASS_NAMES, className)
	});
}
/**
* One report, in one of four status tones (R18.1).
*
* The element is the primitive's `li`. The report itself is announced through a visually
* hidden live region the primitive renders in the {@link ToastProvider}'s announcer
* rather than through a role on this element, so the whole report is read when the
* primitive opens it without this module reading a thing. Everything the toast *does* is
* the primitive's and the user agent's: the open and the timed dismissal, the pause on
* hover and on focus, the swipe to dismiss and the announcement to assistive technology
* (R18.6). Nothing here reimplements any of it, which is invariant 8 of `AGENTS.md`.
*
* The timer that dismisses a toast is the primitive's, governed by `duration` on the
* {@link ToastProvider} or on the toast itself. A caller demonstrating a toast statically
* — a fixture, which R11.11 requires to read no clock — renders it `open` with the
* provider's `duration={Infinity}`, which disables the timer so the surface is still and
* measurable rather than dismissing itself after a sampled interval.
*
* `className` is composed through `cn` with the defaults first (R10.4). `tone` is the
* supported way to change a toast's appearance.
*/
function Toast({ tone = "informational", className, children, ...properties }) {
	return /* @__PURE__ */ jsxs(ToastPrimitive.Root, {
		"data-slot": "toast",
		...properties,
		className: cn(toastClassNames({ tone }), className),
		children: [/* @__PURE__ */ jsx(Icon, {
			icon: TONE_ICON[tone],
			size: "small",
			className: "mt-hurdl-1"
		}), children]
	});
}
/**
* What a {@link Toast} is reporting: the primitive's title, and the toast's accessible
* name (R10.1).
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function ToastTitle({ className, ...properties }) {
	return /* @__PURE__ */ jsx(ToastPrimitive.Title, {
		"data-slot": "toast-title",
		...properties,
		className: cn(TOAST_TITLE_CLASS_NAMES, className)
	});
}
/**
* Supporting prose beneath a {@link ToastTitle}: the primitive's description (R10.1).
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function ToastDescription({ className, ...properties }) {
	return /* @__PURE__ */ jsx(ToastPrimitive.Description, {
		"data-slot": "toast-description",
		...properties,
		className: cn(TOAST_DESCRIPTION_CLASS_NAMES, className)
	});
}
/**
* The control that dismisses a {@link Toast}: the primitive's `button`, carrying the
* mark and an accessible label (R10.1).
*
* The primitive renders a native `button` and closes the toast on activation, so
* dismissal is operable from the keyboard and by pointer without this module handling an
* event. It shows the platform's close glyph — Lucide's `X` through {@link Icon}, drawn
* decoratively since the control's name is on the button — and carries an `aria-label`
* of "Dismiss", written before the caller's properties are spread as a default a caller
* may replace, so a screen-reader user hears a word rather than a glyph. A caller
* passing children replaces the glyph entirely.
*
* `className` is composed through `cn` with the defaults first (R10.4).
*/
function ToastClose({ className, children, ...properties }) {
	return /* @__PURE__ */ jsx(ToastPrimitive.Close, {
		"aria-label": "Dismiss",
		"data-slot": "toast-close",
		...properties,
		className: cn(TOAST_CLOSE_CLASS_NAMES, className),
		children: children ?? /* @__PURE__ */ jsx(Icon, {
			icon: X,
			size: "small"
		})
	});
}
//#endregion
export { Alert, AlertDescription, AlertTitle, Badge, Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, DARK_MODE_CLASS_NAME, DEFAULT_THEME, DEFAULT_THEME_MODE, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, Dropzone, FormField, Icon, Input, InstantDisplay, Label, ScrollArea, ScrollBar, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Separator, Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger, Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, Skeleton, Stack, Switch, THEME_CLASS_NAME_PREFIX, Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow, ThemeSurface, Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, themeClassNamesOf, useSidebar };

//# sourceMappingURL=index.mjs.map